import { z } from 'zod';
import { invoiceIdentityFieldSchema } from './invoiceSelectiveHistoryContracts';
import { sendEvidenceSchema } from './sendEvidence';

// ============================================================================
// IMPORTANT: These schemas match the BACKEND API RESPONSES, not the database schema
// Backend often omits tenant_id, joins data, and calculates fields
// See: docs/CLOUD_SQL_SCHEMA.md for database schema
// See: backend/models/*.py for actual API response structure
// ============================================================================

// ============================================================================
// Projects
// ============================================================================

export const projectSchema = z.object({
  id: z.string().uuid(),
  // tenant_id is NOT returned by backend API (omitted for security)
  // Phase 9.6a: project_number changed from number to string for prefix support (e.g., "CHR-1047")
  project_number: z.string().nullable().optional(), // Phase 7.5.13: NULL for proposals/templates
  // Phase 9.6a: proposal_number for tracking proposal sequence (e.g., "P-001")
  proposal_number: z.string().nullable().optional(),
  organization_id: z.string().uuid().nullable().optional(), // Phase 5: replaces client_id
  client_name: z.string().nullable().optional(), // From LEFT JOIN organizations table (backward compatible alias)
  client_contact_id: z.string().uuid().nullable().optional(), // Phase 7.5.5: Contact person ID
  client_contact_name: z.string().nullable().optional(), // Phase 7.5.5: Contact person name
  // v0.14 Group 9 (declared 2026-06-03): the backend list endpoint has returned
  // these since the multi-client work, but they were never declared here, so
  // Zod stripped them on parse — ProjectCombobox's secondary-client search and
  // "Primary +N more" label silently fell back to client_name (same trap as
  // V108 below / the invoice previous_status bug).
  client_names: z.array(z.string().nullable()).nullable().optional(), // All client org names, primary first
  client_count: z.number().int().nullable().optional(), // Distinct client orgs on the project
  // Detail endpoint org-contact context (backend returns; declared 2026-06-03
  // so future readers aren't silently undefined — previously stripped, unread).
  client_email: z.string().nullable().optional(),
  client_phone: z.string().nullable().optional(),
  client_payment_terms: z.number().int().nullable().optional(),
  name: z.string().min(1).max(1000),
  address: z.string().nullable().optional(), // Can be null in DB
  // v0.12.24: Project-scoped billing address override. NULL means use address as
  // billing default (backwards compatible). Site-semantic surfaces (proposal PDFs,
  // draw certs, weather geocoding) keep reading address.
  billing_address: z.string().nullable().optional(),
  // SPEC-project-po-number-2026-08-17 R1: client-issued PO number (the buyer's
  // own authorisation/budget reference). NULL means the project has no PO.
  // Distinct from project_number (Banks' own sequence) -- see ProjectForm's
  // field label. Never coerced to a number.
  po_number: z.string().nullable().optional(),
  // Phase 9.5f: status field removed - workflow_stage is now the sole state indicator
  workflow_stage: z.string().nullable().optional(), // Phase 5: project workflow stage
  notes: z.string().nullable().optional(), // Can be null in DB
  category: z.string().nullable().optional(), // Phase 7.5.2: Tenant-defined label
  // Phase 7 Batch 7.3: Proposal frontend fields
  is_template: z.boolean().nullable().optional(),
  fee_assumptions: z.string().nullable().optional(),
  project_understanding: z.string().nullable().optional(), // Phase 7.3.2: Project context/scope
  proposal_sent_at: z.string().nullable().optional(), // ISO datetime string
  proposal_accepted_at: z.string().nullable().optional(), // ISO datetime string
  proposal_declined_at: z.string().nullable().optional(), // ISO datetime string - Phase 9.6
  // V108: Soft-delete archive flags. The backend returns these on every
  // project/proposal; without declaring them here Zod strips them on parse,
  // which silently disables the detail-page Restore banner/menu (project.is_archived
  // is always undefined). See ProjectDetailPage archived banner + Restore action.
  is_archived: z.boolean().nullable().optional(),
  archived_at: z.string().nullable().optional(), // ISO datetime string
  created_at: z.string(),
  updated_at: z.string(),
  // Calculated fields (from billable_items joins)
  confirmed_fee: z.number().optional(),
  invoiced_fee: z.number().optional(),
  remaining_fee: z.number().optional(),
  paid_fee: z.number().optional(),
  unpaid_fee: z.number().optional(),
  billable_items_count: z.number().nullable().optional(),
  fully_billed: z.boolean().nullable().optional(),
  // Issue #14: Amount columns (canonical names from backend schema)
  confirmed_total: z.number().optional(),
  invoiced_total: z.number().optional(),
  unbilled_total: z.number().optional(),
  paid_total: z.number().optional(), // Phase 7.5.14: Sum of invoice.amount_paid (includes partial payments)
  // Phase 7 Batch 7.5.6: Proposed totals for templates/proposals
  proposed_total: z.number().optional(),
  proposed_required_total: z.number().optional(),
  // Phase 5 Batch 3: Action owner fields
  waiting_on_us_count: z.number().optional(),
  waiting_on_them_count: z.number().optional(),
  // Phase 7 Batch 7.3.4: Templates table counts
  scope_count: z.number().int().optional(),
  billable_item_count: z.number().int().optional(),
  // V120: CA scope configuration (JSONB)
  ca_scope: z.record(z.string(), z.unknown()).nullable().optional(),
  // R1: Per-project add-in visibility overrides. Shape: {"overrides": {<section_key>: bool}}
  section_config: z.record(z.string(), z.unknown()).nullable().optional(),
});

export type Project = z.infer<typeof projectSchema>;

export const projectCreateSchema = z.object({
  // Phase 9.6a: project_number changed from number to string for prefix support (e.g., "CHR-1047")
  project_number: z.string().nullable().optional(), // Phase 7.5.13: NULL for proposals/templates
  organization_id: z.string().uuid().nullable().optional(),
  client_contact_id: z.string().uuid().nullable().optional(), // Phase 7.5.5: Contact person ID
  name: z.string().min(1, "Project name is required").max(1000),
  address: z.string().nullable().optional(),
  // v0.12.24: Project-scoped billing address override (separate from site address)
  billing_address: z.string().nullable().optional(),
  // SPEC-project-po-number-2026-08-17 R1: client-issued PO number, settable at
  // setup time (creation) or later (edit). Same field/rules as projectSchema above.
  // Empty-string -> null so a cleared field genuinely persists NULL ("no PO"),
  // matching the migration's COMMENT ON COLUMN semantics -- not an empty
  // string sitting in the column looking unset to a naive IS NULL check.
  po_number: z.string().nullable().optional().transform((v) => (v === '' ? null : v)),
  // Phase 9.5f: status field removed - workflow_stage is now the sole state indicator
  workflow_stage: z.string().nullable().optional(), // Phase 5 Batch 3: Project workflow stage
  notes: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  // Phase 7 Batch 7.3: Proposal frontend fields
  is_template: z.boolean().default(false),
  fee_assumptions: z.string().nullable().optional(),
  project_understanding: z.string().nullable().optional(), // Phase 7.3.2: Project context/scope
  // V120: CA scope configuration (JSONB)
  ca_scope: z.record(z.string(), z.unknown()).nullable().optional(),
  // V129: Renewal-aware project creation — permit auto-created server-side when present
  renewal_data: z.object({
    permit_type_definition_id: z.string().nullable(),
    expiry_date: z.string(),
    recurring_period: z.string().nullable(),
    reminder_days: z.array(z.number().int()),
  }).nullable().optional(),
}).refine((data) => {
  // Migration V012: Templates must not have organization_id, projects must have it
  if (!data.is_template && !data.organization_id) {
    return false;
  }
  return true;
}, {
  message: "Client is required",
  path: ["organization_id"],
});

export type ProjectCreate = z.infer<typeof projectCreateSchema>;

export const projectUpdateSchema = projectCreateSchema.partial();
export type ProjectUpdate = z.infer<typeof projectUpdateSchema>;

export const projectListResponseSchema = z.object({
  success: z.boolean(),
  projects: z.array(projectSchema),
  total: z.number(),
});

export type ProjectListResponse = z.infer<typeof projectListResponseSchema>;

export const projectResponseSchema = z.object({
  success: z.boolean(),
  project: projectSchema,
  tenant_id: z.string().optional(),
  timestamp: z.string().optional(),
  permit_created: z.boolean().optional(),
  // v0.17.16: set when renewal details were sent but the renewal was not saved.
  renewal_warning: z.string().nullable().optional(),
});

export type ProjectResponse = z.infer<typeof projectResponseSchema>;

// ============================================================================
// Project Categories (Phase 7.5.2)
// ============================================================================

// Phase 9.6a: number_prefix's maximum length. This is the CANONICAL value —
// the backend enforces the identical limit server-side
// (schemas/project_categories.py's ProjectCategoryOptionCreate/Update
// `number_prefix: Field(max_length=...)`, set in commit 7b846c0d when this
// field was introduced). There is no codegen link between the Python and
// TypeScript definitions, so if either limit ever changes, change both and
// keep them equal.
//
// 2026-09-13 (v0.17 preview walk): the settings dialog's <Input maxLength>
// had drifted to 5 — a different, unexplained number introduced two days
// after this 10-char contract was authored — so typing a 6-character prefix
// silently dropped the extra characters with no error. Both frontend
// surfaces (the dialog's live validation and this schema) now reference this
// single constant so they cannot drift from the backend's contract again.
export const PROJECT_CATEGORY_NUMBER_PREFIX_MAX_LENGTH = 10;

export const projectCategoryOptionSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string(),
  value: z.string(),
  display_order: z.number().int(),
  is_active: z.boolean(),
  // Phase 9.6a: Prefix-based project numbering
  number_prefix: z.string().nullable().optional(),
  next_project_number: z.number().int().nullable().optional(),
  // Phase 11+: Renewal tracking (backend task 9617f945)
  renewal_tracking_enabled: z.boolean().default(false),
  default_recurring_period: z.string().nullable().optional(),
  default_reminder_days: z.array(z.number().int()).nullable().optional(),
  renewal_permit_type: z.string().nullable().optional(),
  // Spec: keystone-client-required-per-category (R2a)
  client_required: z.boolean().default(false).optional(),
  // R1: Default add-in sections for this category (list of section keys). NULL = all add-ins enabled.
  default_sections: z.array(z.string()).nullable().optional(),
  // R2 (SPEC-bc-pst-tax-engine-2026-08-04, AC28): default tax_class for NEW
  // billable items on projects of this category. NULL = unset (ships unset --
  // no category is seeded to a PST-bearing value by this ring).
  default_tax_class: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type ProjectCategoryOption = z.infer<typeof projectCategoryOptionSchema>;

export const projectCategoryOptionListResponseSchema = z.object({
  success: z.boolean(),
  categories: z.array(projectCategoryOptionSchema),
  total: z.number().optional(),
});

export type ProjectCategoryOptionListResponse = z.infer<typeof projectCategoryOptionListResponseSchema>;

export const projectCategoryOptionCreateSchema = z.object({
  value: z.string().min(1).max(50),
  display_order: z.number().int().nonnegative().optional(),
  is_active: z.boolean().optional(),
  // Phase 9.6a: Prefix-based project numbering
  number_prefix: z.string().max(PROJECT_CATEGORY_NUMBER_PREFIX_MAX_LENGTH).nullable().optional(),
  // Spec: keystone-client-required-per-category (R2a)
  client_required: z.boolean().optional(),
  // R4: Default add-in sections for new projects in this category
  default_sections: z.array(z.string()).nullable().optional(),
  // R2 (SPEC-bc-pst-tax-engine-2026-08-04, AC28): editable per category, so
  // the architectural/liquor mapping is CONFIGURATION, not a deploy.
  default_tax_class: z.string().nullable().optional(),
});

export type ProjectCategoryOptionCreate = z.infer<typeof projectCategoryOptionCreateSchema>;
export type ProjectCategoryOptionUpdate = Partial<ProjectCategoryOptionCreate>;

// ============================================================================
// Organizations (Phase 5 Batch 2: Renamed from Clients)
// ============================================================================

export const organizationSchema = z.object({
  id: z.string().uuid(),
  // tenant_id is NOT returned by backend API
  name: z.string().min(1).max(200),

  // Phase 5 Batch 2: Role flags (organization can have multiple roles)
  is_self: z.boolean().nullable().optional(), // Is this our own organization?
  is_client: z.boolean().nullable().optional(), // Client role
  is_consultant: z.boolean().nullable().optional(), // Consultant role
  is_government: z.boolean().nullable().optional(), // Government agency role

  // Contact and business info
  legal_name: z.string().nullable().optional(), // Phase 5 Batch 1: Legal entity name
  email: z.string().nullable().optional(), // Accept any string from API (validation happens on input)
  phone: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  billing_address: z.string().nullable().optional(), // Phase 5 Batch 1: Separate billing address
  notes: z.string().nullable().optional(), // Phase 5 Batch 1: Notes
  payment_terms: z.number().int().nullable().optional(), // Days (e.g., 30 for Net 30)
  specialty: z.string().nullable().optional(), // For consultants (e.g., "Structural", "Electrical")
  category_id: z.string().uuid().nullable().optional(), // Phase 6 Batch 2: Organization category ID (FK to tenant_organization_categories)
  establishments_count: z.number().int().nullable().optional(), // Phase 9.22c: Count of DBAs/establishments

  created_at: z.string(),
  updated_at: z.string(),
});

export type Organization = z.infer<typeof organizationSchema>;

export const organizationCreateSchema = z.object({
  name: z.string().min(1, "Client name is required").max(200),

  // Phase 5 Batch 2: Role flags (at least one must be true)
  is_self: z.boolean().default(false),
  is_client: z.boolean().default(false),
  is_consultant: z.boolean().default(false),
  is_government: z.boolean().default(false),

  legal_name: z.string().nullable().optional(),
  email: z.string().email("Invalid email address").nullable().optional(),
  phone: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  billing_address: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  payment_terms: z.number().int().positive().nullable().optional(),
  specialty: z.string().nullable().optional(),
  category_id: z.string().uuid().nullable().optional(), // Phase 6 Batch 2: Organization category ID
});

export type OrganizationCreate = z.infer<typeof organizationCreateSchema>;

export const organizationUpdateSchema = organizationCreateSchema.partial();
export type OrganizationUpdate = z.infer<typeof organizationUpdateSchema>;

// Organization Form Schema (for UI forms - uses string for payment_terms)
export const organizationFormSchema = z.object({
  name: z.string().min(1, 'Client name is required').max(200),

  // Role flags (Phase 5 Batch 6 Phase 4)
  is_client: z.boolean().default(false),
  is_consultant: z.boolean().default(false),
  is_government: z.boolean().default(false),

  legal_name: z.string().optional(),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  phone: z.string().optional(),
  address: z.string().optional(),
  billing_address: z.string().optional(),
  notes: z.string().optional(),
  payment_terms: z.string().optional(), // String in form, converted to number on submit
  category_id: z.string().optional(), // Phase 6 Batch 2: Organization category ID (optional)
}).refine(
  (data) => data.is_client || data.is_consultant || data.is_government,
  { message: "At least one client role must be selected", path: ["is_client"] }
);

export type OrganizationFormData = z.infer<typeof organizationFormSchema>;

export const organizationListResponseSchema = z.object({
  success: z.boolean(),
  organizations: z.array(organizationSchema),
  total: z.number().optional(), // Backend returns 'total'
  count: z.number().optional(), // Backward compatibility
});

export type OrganizationListResponse = z.infer<typeof organizationListResponseSchema>;

// Backward compatibility: Client types are aliases for Organization types
export const clientSchema = organizationSchema;
export type Client = Organization;
export const clientCreateSchema = organizationCreateSchema;
export type ClientCreate = OrganizationCreate;
export const clientUpdateSchema = organizationUpdateSchema;
export type ClientUpdate = OrganizationUpdate;
// Client list response uses 'clients' field instead of 'organizations'
export const clientListResponseSchema = z.object({
  success: z.boolean(),
  clients: z.array(organizationSchema),
  total: z.number().optional(),
  count: z.number().optional(),
});
export type ClientListResponse = z.infer<typeof clientListResponseSchema>;

// ============================================================================
// Billable Items
// ============================================================================

export const billableItemSchema = z.object({
  id: z.string().uuid(),
  // tenant_id is NOT returned by backend API
  project_id: z.string().uuid(),
  // Phase 9.6a: project_number changed from number to string for prefix support
  project_number: z.string().nullable().optional(), // Joined from projects
  project_name: z.string().nullable().optional(), // Joined from projects
  client_name: z.string().nullable().optional(), // Joined from clients via projects
  description: z.string().min(1).max(5000),
  deliverables: z.string().nullable().optional(), // Phase 8.3: Deliverables output (shown in proposal PDFs)
  amount: z.number(), // Phase 9.29: Allow negative for credits/adjustments (e.g., "Less $192 overpayment")

  // Phase 7 Batch 2: Added internal_work category
  // QW-3: Added renewal category (Christa feedback 2024-12-22)
  // Tax calculated at INVOICE time, not stored on billable items (Migration 020)
  category: z.enum(['professional_fee', 'add_service', 'renewal', 'billable_expense', 'pass_through', 'internal_work']),

  // R2 (SPEC-bc-pst-tax-engine-2026-08-04, V218): service tax class for the
  // PST engine. SEPARATE from category (which conflates taxability with
  // revenue-vs-expense). NULL = unclassified (GST only in rules mode).
  // Defaults from the project's category setting at creation time.
  tax_class: z.string().nullable().optional(),

  // Migration 013: Replaced status enum with cancelled boolean
  cancelled: z.boolean().nullable().optional(), // Simple boolean - cancelled or not (replaces status field)

  // Phase 7 Batch 2: Status tracking fields
  status: z.enum(['not_started', 'in_progress', 'complete', 'on_hold']).nullable().optional(),
  status_notes: z.string().nullable().optional(),

  // Phase 7 Batch 7.1: Lifecycle status (proposed vs confirmed)
  item_status: z.enum(['proposed', 'proposed-optional', 'confirmed', 'declined']).nullable().optional(),

  // Migration V011: Due date tracking
  due_date: z.string().nullable().optional(), // ISO date string (YYYY-MM-DD)

  // Phase 7.5.12: Expected invoice month (cash flow planning)
  scheduled_month: z.string().nullable().optional(), // ISO date string (YYYY-MM-DD)

  // 9.9d CONTRACT-02: Display order for drag-drop reordering (Migration V053)
  display_order: z.number().int().nullable().optional(),

  // 10c.2 - V089: Recurring/partnership fee (proposal-only)
  amount_recurring: z.number().nullable().optional(),
  recurring_interval: z.enum(['monthly', 'quarterly', 'annual']).nullable().optional(),

  created_at: z.string(),
  updated_at: z.string(),
  // Migration 013: Calculated fields (enriched by backend API)
  total_amount: z.number().optional(), // Same as amount (for consistency)
  invoiced_amount: z.number().optional(), // Sum of invoice_items amounts
  unbilled_amount: z.number().optional(), // Total - invoiced
  paid_amount: z.number().optional(), // Sum where invoice status = 'paid'
  unpaid_amount: z.number().optional(), // Invoiced - paid
  invoiced_percentage: z.number().optional(), // (invoiced / total) * 100
  display_state: z.enum([
    'cancelled',           // cancelled = true
    'unbilled',           // invoiced = 0
    'partially_invoiced', // invoiced > 0 but < total
    'invoiced',           // invoiced = total, paid = 0
    'partially_paid',     // paid > 0 but < invoiced
    'paid'                // paid = invoiced
  ]).optional(), // Calculated from amounts and cancelled flag
  // Legacy field for backward compatibility during migration
  remaining_amount: z.number().optional(), // Alias for unbilled_amount
});

export type BillableItem = z.infer<typeof billableItemSchema>;

export const billableItemCreateSchema = z.object({
  project_id: z.string().uuid(),
  source_template_id: z.string().uuid().optional(),
  // Phase 9.6a: project_number changed from number to string for prefix support
  // Phase 8.3: project_number optional for proposals/templates (they have NULL project_number)
  project_number: z.string().nullable().optional(),
  description: z.string().min(1, "Description is required").max(5000),
  // Phase 8.3: Deliverables output (shown in proposal PDFs)
  // Handle empty strings from cleared form inputs → normalize to null
  // IMPORTANT: Must use null (not undefined) so field is included in payload when clearing
  deliverables: z.union([z.string(), z.literal(''), z.null()]).optional().transform(val => val || null),
  amount: z.number(), // Phase 9.29: Allow negative for credits/adjustments

  // Phase 7 Batch 2: Added internal_work category
  // QW-3: Added renewal category (Christa feedback 2024-12-22)
  // Tax calculated at INVOICE time, not stored on billable items (Migration 020)
  category: z.enum(['professional_fee', 'add_service', 'renewal', 'billable_expense', 'pass_through', 'internal_work'])
    .default('professional_fee'),

  // R2 (SPEC-bc-pst-tax-engine-2026-08-04, AC30): per-item tax-class override
  // (InvoiceItemRow.tsx's handleTaxClassChange PATCHes this). Mirrors
  // billableItemSchema's read-side `tax_class` above — omitted here meant
  // TypeScript accepted a payload the write path could never actually send.
  tax_class: z.string().nullable().optional(),

  // Migration 013: Replaced status enum with cancelled boolean
  cancelled: z.boolean().default(false), // Default to not cancelled

  // Phase 7 Batch 2: Status tracking fields
  status: z.enum(['not_started', 'in_progress', 'complete', 'on_hold']).default('not_started'),
  // Handle empty strings from cleared form inputs → normalize to null
  // IMPORTANT: Must use null (not undefined) so field is included in payload when clearing
  status_notes: z.union([z.string(), z.literal(''), z.null()]).optional().transform(val => val || null),

  // Phase 7 Batch 7.1: Lifecycle status
  item_status: z.enum(['proposed', 'proposed-optional', 'confirmed', 'declined'])
    .default('confirmed'),

  // Migration V011: Due date tracking
  // Handle empty strings from cleared form inputs → normalize to null
  // IMPORTANT: Must use null (not undefined) so field is included in payload when clearing
  due_date: z.union([z.string(), z.literal(''), z.null()]).optional().transform(val => val || null),

  // Phase 7.5.12: Expected invoice month (cash flow planning)
  // Handle empty strings from cleared form inputs → normalize to null
  // IMPORTANT: Must use null (not undefined) so field is included in payload when clearing
  // Validate YYYY-MM format for cross-browser compatibility (Safari doesn't support input[type=month])
  scheduled_month: z.union([
    z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Month must be in YYYY-MM format (e.g., 2025-01)"),
    z.literal(''),
    z.null()
  ]).optional().transform(val => val || null),

  // 9.9d CONTRACT-02: Display order for drag-drop reordering (Migration V053)
  display_order: z.number().int().nonnegative().optional(),

  // 10c.2 - V089: Recurring/partnership fee (proposal-only, never invoiced)
  amount_recurring: z.number().nonnegative().nullable().optional(),
  recurring_interval: z.enum(['monthly', 'quarterly', 'annual']).nullable().optional(),
}).refine(
  (data) => data.category !== 'internal_work' || data.amount === 0,
  { message: "Internal work items must have amount = $0", path: ["amount"] }
).refine(
  (data) => data.category === 'internal_work' || data.amount > 0,
  { message: "Amount is required and must be greater than zero", path: ["amount"] }
).refine(
  (data) => !data.amount_recurring || data.amount_recurring === 0 || !!data.recurring_interval,
  { message: "Interval is required when a recurring amount is set", path: ["recurring_interval"] }
).refine(
  (data) => !data.recurring_interval || (!!data.amount_recurring && data.amount_recurring > 0),
  { message: "Recurring amount must be greater than zero when an interval is set", path: ["amount_recurring"] }
);

export type BillableItemCreate = z.infer<typeof billableItemCreateSchema>;

export const billableItemUpdateSchema = billableItemCreateSchema.partial().extend({
  // R5 AC4: which invoice is being edited, and the other invoices she ticked for a shared description edit.
  edited_invoice_id: z.string().optional(),
  description_invoice_ids: z.array(z.string()).optional(),
  // M4: the ticked sent / partly paid / paid invoices she knowingly changes; the server refuses them without it.
  acknowledge_sent_invoice_ids: z.array(z.string()).optional(),
});
export type BillableItemUpdate = z.infer<typeof billableItemUpdateSchema>;

export const billableItemListResponseSchema = z.object({
  success: z.boolean(),
  billable_items: z.array(billableItemSchema),
  total: z.number(), // Backend returns 'total' (consistent with invoices/organizations)
});

export type BillableItemListResponse = z.infer<typeof billableItemListResponseSchema>;

// ============================================================================
// Invoices
// ============================================================================

export const invoiceEffectiveIdentitySchema = z.object({
  legal_name: invoiceIdentityFieldSchema,
  current_legal_name: invoiceIdentityFieldSchema,
});
export type InvoiceEffectiveIdentity = z.infer<typeof invoiceEffectiveIdentitySchema>;

export const invoiceSchema = z.object({
  // GET projects the canonical PDF legal identity without changing client aliases.
  identity_effective: invoiceEffectiveIdentitySchema.optional(),
  id: z.string().uuid(),
  // tenant_id is NOT returned by backend API
  invoice_number: z.string().min(1).max(50),
  project_id: z.string().uuid(),
  organization_id: z.string().uuid().nullable().optional(), // Phase 5: Organization to invoice (nullable for legacy data)
  client_scope_id: z.string().uuid().nullable().optional(), // Phase 9.7bc: Client scope selection
  // Phase 9.6a: project_number changed from number to string for prefix support
  project_number: z.string().nullable().optional(), // Joined from projects
  project_name: z.string().nullable().optional(), // Joined from projects
  client_name: z.string().nullable().optional(), // Joined from clients
  // Phase 9.15a: Organization fields for invoice page display
  client_legal_name: z.string().nullable().optional(),
  client_address: z.string().nullable().optional(),
  client_billing_address: z.string().nullable().optional(),
  // Phase 7.5.19 + Phase 9.15b: Contact info with ID for edit modal
  client_contact_id: z.string().uuid().nullable().optional(),
  client_contact_name: z.string().nullable().optional(),
  client_contact_email: z.string().nullable().optional(),
  client_contact_phone: z.string().nullable().optional(),
  client_contact_title: z.string().nullable().optional(),
  client_is_primary_contact: z.boolean().nullable().optional(),
  invoice_date: z.string().nullable().optional(), // ISO date string
  due_date: z.string().nullable().optional(), // ISO date string
  fee_amount: z.number().nullable().optional(), // Sum of invoice item amounts (before tax)
  tax_amount: z.number().nullable().optional(), // Sum of invoice item tax amounts
  total_amount: z.number(), // fee_amount + tax_amount
  amount_paid: z.number().nullable().optional(), // Track partial payments
  payment_date: z.string().nullable().optional(), // QF-08: Date payment was received (ISO date string)
  // V168 (2026-05-12): Added 'partially_paid' — backend auto-promotes when 0 < amount_paid < total_amount.
  status: z.enum(['draft', 'ready', 'sent', 'paid', 'partially_paid', 'overdue', 'cancelled']),
  display_status: z.string().optional(), // Calculated (e.g., "overdue" instead of "sent")
  // 2026-06-03: Backend has always returned these, but they were undeclared
  // here, so fetchAPI's Zod parse silently stripped them (same trap as the
  // #150 dead Restore UI). previous_status drives the void-dialog copy (M1
  // tests); status_changed_at is the Sent-column fallback in the list;
  // project_is_archived renders the list's "Archived" chip. Kept lenient
  // (string, not enum) so an unexpected legacy value can't fail the fetch.
  previous_status: z.string().nullable().optional(),
  status_changed_at: z.string().nullable().optional(), // ISO datetime string
  status_changed_by: z.string().nullable().optional(),
  project_is_archived: z.boolean().nullable().optional(), // Joined from projects.is_archived
  external_notes: z.string().nullable().optional(), // Phase 6: Client-facing notes (appears on PDF)
  internal_notes: z.string().nullable().optional(), // Phase 6: Internal team communication (NOT on PDF)

  // Phase 6: PDF Generation metadata (system-managed, read-only)
  pdf_storage_path: z.string().nullable().optional(), // Firebase Storage path
  pdf_generated_at: z.string().nullable().optional(), // ISO datetime string
  pdf_version: z.number().nullable().optional(), // Regeneration counter
  pdf_data_hash: z.string().nullable().optional(), // SHA256 hash for sync detection

  // Phase 9.1b: Email send tracking (QW-05)
  email_sent_at: z.string().nullable().optional(), // ISO datetime string
  // R3 AC9: the one send rule decided on the server (services/invoice_send_evidence.py). Every screen that says
  // "sent" reads this, never email_sent_at or status_changed_at on its own.
  send_evidence: sendEvidenceSchema.nullable().optional(),
  email_sent_to: z.string().nullable().optional(), // Email address
  email_sent_by: z.string().nullable().optional(), // User email who sent
  email_sent_count: z.number().int().nullable().optional(), // Number of times emailed
  email_last_note: z.string().nullable().optional(), // Custom note from last email

  // Phase 9.18d: Establishment (DBA) for this invoice
  establishment_id: z.number().int().nullable().optional(),
  establishment_name: z.string().nullable().optional(), // Joined from organization_establishments
  // Phase 9.26: Establishment legal_name (separate from COALESCE'd client_legal_name)
  establishment_legal_name: z.string().nullable().optional(),
  // The invoice-list preview must see these raw identity sources. The backend
  // selects both; omitting them here made Zod strip the per-invoice override.
  org_legal_name: z.string().nullable().optional(),
  frozen_legal_name: z.string().nullable().optional(),
  // v0.12.22 Fix 2: project_scope's current default establishment (read-only)
  // Used by FE to detect divergence and show the "Apply this change to the
  // project too?" dialog when the invoice's establishment is changed.
  scope_establishment_id: z.number().int().nullable().optional(),
  scope_establishment_name: z.string().nullable().optional(),

  // Phase 9.18j: Direct contact assignment for this invoice
  contact_id: z.string().uuid().nullable().optional(),

  // Phase 9.21g: Billing address source selector
  // V157: Added 'establishment' source — resolves to organization_establishments.address
  billing_address_source: z.enum(['organization', 'project', 'custom', 'establishment']).nullable().optional(),
  billing_address_override: z.string().nullable().optional(), // Custom address (when source='custom')
  frozen_billing_address: z.string().nullable().optional(), // Reviewed per-invoice address; explicit blank is meaningful.
  org_billing_address: z.string().nullable().optional(), // From organization.billing_address
  org_address: z.string().nullable().optional(), // From organization.address
  project_address: z.string().nullable().optional(), // From projects.address
  // V157: Establishment-level defaults
  establishment_address: z.string().nullable().optional(), // From organization_establishments.address
  establishment_hide_dba_default: z.boolean().nullable().optional(),

  // Phase 9.30: Hide DBA on invoice PDF
  hide_dba: z.boolean().nullable().optional(),

  // V175 (bill-to suppress): per-invoice opt-out for the empty-TO Attention fallback
  suppress_attention_line: z.boolean().nullable().optional(),

  // Phase 11a: Draw invoice fields
  currency: z.string().nullable().optional(),
  invoice_type: z.string().nullable().optional(),
  sov_id: z.string().uuid().nullable().optional(),
  draw_number: z.number().int().nullable().optional(),

  // Phase 11e: Dropbox sync
  dropbox_synced_at: z.string().nullable().optional(), // ISO datetime string
  dropbox_path: z.string().nullable().optional(), // Full Dropbox path for clickable link

  // R1 (SPEC-qbo-csv-harden-2026-06-20): round-trip export marker. Cancel-invoice
  // feature reads this to warn "this was already exported to QuickBooks -- void
  // it there too" in CancelInvoiceDialog.
  qbo_csv_exported_at: z.string().nullable().optional(), // ISO datetime string

  // V185 (qbo-discrete R2): per-invoice approve-before-push gate. Backend
  // get_invoice_by_id SELECTs both columns, so these survive the Zod boundary.
  // NULL qbo_sync_approved_at means the invoice has NOT been approved for the
  // discrete QBO push and SyncToQBButton blocks until it is.
  qbo_sync_approved_at: z.string().nullable().optional(), // ISO datetime string
  qbo_sync_approved_by: z.string().nullable().optional(), // approver email

  // R7: invoice_contacts snapshot (TO/CC recipients) returned by list endpoint
  invoice_contacts: z.array(z.object({
    role: z.enum(['to', 'cc']),
    contact_name: z.string(),
    contact_email: z.string().nullable().optional(),
  })).optional(),

  // SPEC-invoice-merge-2026-08-17 R2 (V216): set when this invoice was
  // retired by a merge; points at the surviving invoice. R3 uses this to
  // keep the retired invoice visible (not vanished) in the list — see
  // InvoiceListPage's "Merged" indicator.
  merged_into_invoice_id: z.string().uuid().nullable().optional(),
  // SPEC-invoice-merge-history R3: the survivor's NUMBER, so a search for an original
  // number can say "merged into 0757" without a second fetch.
  merged_into_invoice_number: z.string().nullable().optional(),

  // SPEC-project-po-number-2026-08-17 R2: copied from the project at
  // creation, editable per-invoice thereafter (any status). NULL = this
  // invoice has no PO -- never an empty string (see backend V214 header).
  po_number: z.string().nullable().optional(),
  // Read-only: the project's CURRENT po_number, joined for display so the
  // field can show "inherits <value>" / decide whether to offer the
  // write-back choice without a second fetch. Never used to derive
  // po_number above -- the invoice's own column is never re-read from the
  // project (Rule 3, freeze-on-issue-by-construction).
  project_po_number: z.string().nullable().optional(),

  created_at: z.string(),
  updated_at: z.string(),
});

export type Invoice = z.infer<typeof invoiceSchema>;

export const invoiceCreateSchema = z.object({
  invoice_number: z.string().min(1, "Invoice number is required").max(50),
  project_id: z.string().uuid(),
  organization_id: z.string().uuid(), // Phase 5: Organization to invoice
  // Phase 9.7bc: Client scope selection - links invoice to specific client scope
  client_scope_id: z.string().uuid().nullable().optional(),
  // Phase 9.18d: Establishment (DBA) for this invoice
  establishment_id: z.number().int().nullable().optional(),
  // Phase 9.18j: Contact for this invoice
  contact_id: z.string().uuid().nullable().optional(),
  // Phase 9.6a: project_number changed from number to string for prefix support
  // Phase 8.3: project_number optional for proposals/templates (they have NULL project_number)
  project_number: z.string().nullable().optional(),
  invoice_date: z.string().nullable().optional(),
  due_date: z.string().nullable().optional(),
  // Financial fields - all required (calculated before submission)
  fee_amount: z.number().nonnegative(), // Required: sum of invoice item amounts (before tax)
  tax_amount: z.number().nonnegative(), // Required: sum of invoice item tax amounts
  total_amount: z.number().nonnegative(), // Required: fee + tax
  amount_paid: z.number().nonnegative().nullable().optional(),
  status: z.enum(['draft', 'ready', 'sent', 'paid', 'partially_paid', 'overdue', 'cancelled']).default('draft'),
  external_notes: z.string().nullable().optional(), // Phase 6: Client-facing notes (appears on PDF)
  internal_notes: z.string().nullable().optional(), // Phase 6: Internal team communication (NOT on PDF)
  // Phase 9.30: Hide DBA on invoice PDF
  hide_dba: z.boolean().nullable().optional(),
  // V175 (bill-to suppress): per-invoice opt-out for the empty-TO Attention fallback
  suppress_attention_line: z.boolean().nullable().optional(),
});

export type InvoiceCreate = z.infer<typeof invoiceCreateSchema>;

// R4 (SPEC-christa-contacts-identity): Create Invoice may carry a browser-minted operation key and its
// pre-selected lines in the SAME request. Kept OUT of invoiceCreateSchema on purpose: invoiceUpdateSchema is
// invoiceCreateSchema.partial(), and an update must never accept a key or lines.
export const invoiceCreateLineSchema = z.object({
  billable_item_id: z.string().uuid(),
  description: z.string().optional(),
  amount: z.number().nonnegative(),
  tax_rate: z.number().nonnegative().max(100).optional(),
});
export type InvoiceCreateLine = z.infer<typeof invoiceCreateLineSchema>;
export type InvoiceCreateRequest = InvoiceCreate & {
  operation_key?: string;
  lines?: InvoiceCreateLine[];
};

// R3a (v0.14): Explicit per-field propagation mode for BILL TO writes.
// Generalisation of the v0.12.22 scope_update_mode across establishment,
// address, and legal_name fields. See backend FieldUpdateMode in
// schemas/invoices.py for the legality table. Request-only, no DB analog.
export const fieldUpdateModeSchema = z.enum([
  'invoice_only',
  'save_to_scope',
  'create_new_scope',
  'save_to_project',
  'save_to_establishment',
  // B4 (v0.14, G4 fix-loop 2026-05-29): client-level address default.
  // Writes organizations.billing_address (NOT organizations.address) and
  // flips billing_address_source='organization'. Downstream readers prefer
  // billing_address; writing to `address` would silently mask. See backend
  // FieldUpdateMode comment for full legality table.
  'save_to_organization',
  'save_dba_default',
  'save_org_default',
  'stop_for_dba',
  'stop_for_org',
]);
export type FieldUpdateMode = z.infer<typeof fieldUpdateModeSchema>;

// R3a (v0.14): InvoiceUpdate adds field_update_mode + legal_name (a transit
// field — backend writes organization_establishments.legal_name when both are
// present and field_update_mode='save_to_establishment').
//
// Wave 3 (v0.14 org-change): adds optional client-change precondition fields
// per org-change-edge-cases-v2-2026-05-28.md §3.1:
//   - expected_organization_id  → 412 organization_mismatch on stale tab
//   - archive_source_dba        → §14.7 standalone-DBA path archive choice
//   - establishment_name        → transit hint for legacy save_to_establishment
// IMPORTANT (feedback_mutation_hook_field_drift): keep this schema as a
// union/extension of invoiceCreateSchema.partial() so new optional fields
// pass through any object spread automatically. Do NOT explicit-pick fields
// in mutation hooks — the whole `data` is forwarded to api.invoices.update.
export const invoiceUpdateSchema = invoiceCreateSchema.partial().extend({
  // Optional exact archived copy for the ordinary Mark as Sent transition.
  // Omission means the sent document is unknown; never infer latest export.
  external_send_revision_id: z.string().uuid().optional(),
  field_update_mode: fieldUpdateModeSchema.optional(),
  legal_name: z.string().max(255).optional(),
  // Wave 3 org-change additions:
  expected_organization_id: z.string().uuid().nullable().optional(),
  expected_establishment_id: z.number().int().nullable().optional(),
  establishment_name: z.string().nullable().optional(),
  archive_source_dba: z.boolean().optional(),
  // SPEC-project-po-number-2026-08-17 R2. Deliberately NOT on
  // invoiceCreateSchema (and therefore not inherited via .partial() above)
  // -- copy-down happens automatically at creation (Rule 1); a per-invoice
  // override is something she types AFTER the invoice exists, via this PUT
  // path. Unlike projectSchema/projectCreateSchema's po_number (R1), this
  // field is deliberately NOT `.transform()`-ed here: a Zod `.transform()`
  // on an optional field makes the OUTPUT type non-optional for TS's
  // purposes, which broke `Partial<InvoiceUpdate>` call sites elsewhere
  // (useUpdateInvoiceInline's `data: Partial<InvoiceUpdate>`). The
  // empty-string -> null normalization happens on the BACKEND instead, at
  // the PUT boundary (routers/invoices.py update_invoice) -- same outcome,
  // one fewer place to keep in sync.
  po_number: z.string().nullable().optional(),
  // R2 (Rule 2): set alongside po_number to accept the "apply to this
  // project going forward" write-back offer. Absent/false = decline (just
  // this invoice). See UpdateInvoiceResponse.po_write_back_applied for
  // whether it actually took effect (a project that already has a PO is
  // never silently overwritten).
  apply_po_to_project: z.boolean().optional(),
});
export type InvoiceUpdate = z.infer<typeof invoiceUpdateSchema>;

// Wave 3 (v0.14 org-change) §5.0: cross-entity picker search response.
// One row per match — kind discriminates client (organization) vs DBA
// (organization_establishment). DBA rows include parent-client context.
export const orgChangeSearchResultSchema = z.object({
  kind: z.enum(['org', 'dba']),
  id: z.union([z.string(), z.number()]),
  name: z.string(),
  legal_name: z.string().nullable().optional(),
  parent_org_id: z.string().nullable().optional(),
  parent_org_name: z.string().nullable().optional(),
  is_archived: z.boolean().nullable().optional(),
});
export type OrgChangeSearchResult = z.infer<typeof orgChangeSearchResultSchema>;

export const orgChangeSearchResponseSchema = z.object({
  results: z.array(orgChangeSearchResultSchema),
});
export type OrgChangeSearchResponse = z.infer<typeof orgChangeSearchResponseSchema>;

export const invoiceListResponseSchema = z.object({
  invoices: z.array(invoiceSchema),
  total: z.number(),
  limit: z.number().optional(),
  offset: z.number().optional(),
});

export type InvoiceListResponse = z.infer<typeof invoiceListResponseSchema>;

// ============================================================================
// Invoice Items
// ============================================================================

export const invoiceItemSchema = z.object({
  id: z.string().uuid(),
  // tenant_id is NOT returned by backend API
  invoice_id: z.string().uuid(),
  billable_item_id: z.string().uuid().nullable(), // Can be null for ad-hoc items
  project_id: z.string().uuid().nullable().optional(),
  description: z.string().min(1).max(5000),
  amount: z.number().nonnegative(), // Line item amount before tax
  tax_rate: z.number().nonnegative(), // Tax rate percentage (e.g., 5.00 for 5%)
  tax_amount: z.number().nonnegative(), // Calculated tax for this line
  // Phase 9.34: Optional quantity/rate fields for invoice line item display
  quantity: z.number().positive().nullable().optional(), // Number of units (e.g., 3 hours)
  unit_price: z.number().nonnegative().nullable().optional(), // Price per unit (e.g., $250/hr)
  unit_type: z.enum(['hours', 'days', 'units', 'each']).nullable().optional(), // Type of unit for display
  // Phase 9.15a: Count of invoices this billable item appears on (for multi-invoice warning)
  invoice_count: z.number().int().optional(),
  // R3 AC4: the other LIVE invoices a description edit will change, with whether each has a recorded send or a payment.
  // Cancelled invoices are never listed (the edit never reaches them).
  other_invoices: z.array(z.object({
    invoice_id: z.string(),
    invoice_number: z.string().nullable().optional(),
    status: z.string(),
    send_recorded: z.boolean(),
    payment_state: z.enum(['paid', 'partially_paid']).nullable().optional(),
    // What that invoice bills on this item (lowering the item total never goes below the sum of these).
    billed_amount: z.number().nullable().optional(),
  })).optional(),
  // What the OTHER lines on this same invoice bill on this line's item (models/invoice_items attach). Absent = unknown.
  same_invoice_other_billed: z.number().optional(),
  // R2 (SPEC-bc-pst-tax-engine-2026-08-04): per-tax detail for this line,
  // one row per tax code (attached by models.invoice_items.attach_line_taxes
  // from invoice_item_taxes, or derived from the line's own scalars for
  // legacy lines). Drives the per-distinct-tax totals rows.
  taxes: z.array(z.object({
    tax_code: z.string(),
    label: z.string(),
    rate: z.number(),
    base_fraction: z.number(),
    taxable_base: z.number(),
    amount: z.number(),
  })).optional(),
  // R2 (bc-pst, AC30/AC31): the line's tax class (from its billable item)
  // and the class its project's category currently defaults to. The UI
  // shows the first and badges a DIFFERENCE as a hand override.
  tax_class: z.string().nullable().optional(),
  category_default_tax_class: z.string().nullable().optional(),
  // Phase 9.18b: Category from billable item for service/expense grouping
  billable_item_category: z.enum(['professional_fee', 'add_service', 'renewal', 'billable_expense', 'pass_through', 'internal_work']).nullable().optional(),
  // V234 (QA round 2 on SPEC-v017-completion-2026-09-27 R4): the category
  // THIS LINE is billed under — COALESCE(invoice_items.category,
  // billable_items.category) server-side. The Services-vs-Expenses grouping
  // reads this, so the screen classifies a line exactly the way the rendered
  // PDF does. `billable_item_category` above stays the STORED item's own
  // category; the two are different questions.
  line_category: z.enum(['professional_fee', 'add_service', 'renewal', 'billable_expense', 'pass_through', 'internal_work']).nullable().optional(),
  // Phase 9.27: Current billable item values for split editing behavior
  billable_item_description: z.string().optional(), // Current description from billable_items table
  billable_item_amount: z.number().nonnegative().optional(), // Total billable amount for partial invoice context
  // Christa feedback 2026-04-08: scheduled_month from parent billable item for late-invoice-date warning
  scheduled_month: z.string().nullable().optional(),
  // SPEC-test-suite-honesty R5(b): the get_invoice_items SELECT
  // (models/invoice_items.py) joins projects and returns p.project_number +
  // p.name AS project_name. Both were undeclared, so the parse stripped the
  // line item's project context. Lenient per the v0.14.3 method.
  project_number: z.string().nullable().optional(),
  project_name: z.string().nullable().optional(),
  created_at: z.string(),
});

export type InvoiceItem = z.infer<typeof invoiceItemSchema>;

export const invoiceItemCreateSchema = z.object({
  invoice_id: z.string().uuid(),
  billable_item_id: z.string().uuid().nullable(),
  project_id: z.string().uuid().nullable().optional(),
  description: z.string().min(1, "Description is required").max(5000),
  amount: z.number().positive("Amount is required and must be greater than zero"),
  tax_rate: z.number().nonnegative().default(5.00), // Default to 5% GST
  tax_amount: z.number().nonnegative().optional(), // Auto-calculated if not provided
  // V234 (QA round 2 on SPEC-v017-completion-2026-09-27 R4): the category
  // THIS LINE is billed under. Omitted, the backend snapshots the billable
  // item's own category onto the line. Never written back to the billable
  // item.
  category: z.enum(['professional_fee', 'add_service', 'renewal', 'billable_expense', 'pass_through', 'internal_work']).nullable().optional(),
  // Phase 9.34: Optional quantity/rate fields for invoice line item display
  quantity: z.number().positive().nullable().optional(),
  unit_price: z.number().nonnegative().nullable().optional(),
  unit_type: z.enum(['hours', 'days', 'units', 'each']).nullable().optional(),
});

export type InvoiceItemCreate = z.infer<typeof invoiceItemCreateSchema>;

export const invoiceItemListResponseSchema = z.object({
  success: z.boolean(),
  invoice_items: z.array(invoiceItemSchema),
  total: z.number(),
});

export type InvoiceItemListResponse = z.infer<typeof invoiceItemListResponseSchema>;

// ============================================================================
// Billing Session (Phase 10a.4)
// ============================================================================

export const billingQueueItemSchema = z.object({
  id: z.string().uuid(),
  description: z.string(),
  total_amount: z.number(),
  invoiced_amount: z.number(),
  unbilled_amount: z.number(),
  category: z.enum(['professional_fee', 'add_service', 'renewal', 'billable_expense', 'pass_through', 'internal_work']).nullable(),
});

export type BillingQueueItem = z.infer<typeof billingQueueItemSchema>;

export const billingQueueProjectSchema = z.object({
  project_id: z.string().uuid(),
  project_number: z.string().nullable(),
  project_name: z.string(),
  client_name: z.string().nullable(),
  organization_id: z.string().uuid().nullable(),
  // R2 (invoice-identity-cascade) AC7: the resolved client scope for this queue
  // row (via the deterministic primary rule). The billing session passes it on
  // create so a batch-created invoice inherits the same identity bundle a
  // single-created one would. Multi-client projects emit one row per scope (AC8).
  client_scope_id: z.string().uuid().nullable().optional(),
  unbilled_total: z.number(),
  item_count: z.number(),
  billable_items: z.array(billingQueueItemSchema),
});

export type BillingQueueProject = z.infer<typeof billingQueueProjectSchema>;

export const billingQueueResponseSchema = z.object({
  success: z.boolean(),
  projects: z.array(billingQueueProjectSchema),
  total: z.number(),
});

export type BillingQueueResponse = z.infer<typeof billingQueueResponseSchema>;

// ============================================================================
// Dashboard (existing from current api.ts)
// ============================================================================

export const dashboardPreferencesSchema = z.object({
  user_email: z.string(), // Accept any string from API
  widgets: z.object({
    quick_stats: z.object({ enabled: z.boolean(), size: z.string() }),
    revenue_chart: z.object({ enabled: z.boolean(), size: z.string() }),
    outstanding_invoices: z.object({ enabled: z.boolean(), size: z.string() }),
    project_pipeline: z.object({ enabled: z.boolean(), size: z.string() }),
  }),
  updated_at: z.string(),
});

export type DashboardPreferences = z.infer<typeof dashboardPreferencesSchema>;

// ============================================================================
// Organization Contacts (Phase 5 Batch 3)
// ============================================================================

export const organizationContactSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  name: z.string().min(1).max(200),
  email: z.string().nullable().optional(), // Accept any string from API (validation happens on input)
  phone: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  // SPEC-test-suite-honesty R5(b): the backend get_organization_contacts SELECT
  // (models/organizations.py) returns `notes`, but the READ schema never
  // declared it, so fetchAPI's Zod parse stripped it — the same trap that made
  // notes-on-create silently drop (see organizationContactCreateSchema comment).
  // Lenient (nullable/optional string) per the v0.14.3 method.
  notes: z.string().nullable().optional(),
  // R1 AC17: the Contacts list reads the email from `emails` first (a person added through "Add Accounting
  // Contact" has ONLY `emails`) and falls back to the single `email`; `is_accounting_contact` is what keeps a
  // default contact out of the plain Contacts list.
  emails: z.array(z.string()).nullable().optional(),
  is_accounting_contact: z.boolean().nullable().optional(),
  is_primary_contact: z.boolean().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type OrganizationContact = z.infer<typeof organizationContactSchema>;

export const organizationContactCreateSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional(),
  title: z.string().optional(),
  // v0.14 (2026-05-29): notes is in the backend OrganizationContactCreate
  // schema (via OrganizationContactBase). Was missing on the frontend +
  // explicit-picked out by useCreateOrganizationContact, so notes-on-create
  // silently dropped (cross-cutting hunt S3).
  notes: z.string().max(2000).optional(),
  is_primary_contact: z.boolean().default(false),
  project_id: z.string().uuid().optional(),  // Phase 9.21i: Auto-add to client scope
});

export type OrganizationContactCreate = z.infer<typeof organizationContactCreateSchema>;

export const organizationContactUpdateSchema = organizationContactCreateSchema.partial();
export type OrganizationContactUpdate = z.infer<typeof organizationContactUpdateSchema>;

export const organizationContactListResponseSchema = z.object({
  success: z.boolean(),
  contacts: z.array(organizationContactSchema),
  total: z.number(),
  organization_id: z.string(),
});

export type OrganizationContactListResponse = z.infer<typeof organizationContactListResponseSchema>;

// R1 (hotfix v0.14.5): typed element for the invoice eligible-contacts picker.
// Replaces the prior `z.array(z.any())` on getEligibleContacts — z.any() lets
// fetchAPI silently strip fields (this codebase's signature bug class). The
// shape is the real organization_contacts row the picker consumes across both
// AddInvoiceRecipientPopover and BillToPanel:
//   - emails is the canonical multi-email ARRAY column; `email` is the legacy
//     scalar fallback for rows created before emails[] existed.
//   - is_accounting_contact discriminates the accounting recipient pool from
//     the general "other contacts" pool (the backend normalizes it to a
//     concrete boolean on every returned element).
//   - source: 'establishment' | 'organization' | 'general'.
// Permissive on optional metadata (phone/title/notes/timestamps) so the schema
// never strips real fields, but strict on the fields the picker depends on.
export const eligibleContactSchema = z.object({
  id: z.string(),
  organization_id: z.string().nullable().optional(),
  name: z.string(),
  email: z.string().nullable().optional(),
  emails: z.array(z.string()).nullable().optional(),
  phone: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  is_accounting_contact: z.boolean().nullable().optional(),
  establishment_id: z.number().nullable().optional(),
  source: z.enum(['establishment', 'organization', 'general']).optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export type EligibleContact = z.infer<typeof eligibleContactSchema>;

// ============================================================================
// Organization Establishments (Phase 9.18d - DBA Names)
// ============================================================================

export const establishmentSchema = z.object({
  id: z.number(),
  tenant_id: z.string(),
  organization_id: z.string().uuid(),  // UUID
  name: z.string().min(1).max(200),
  legal_name: z.string().nullable().optional(),  // Phase 9.23a: Override organization legal_name
  is_default: z.boolean().nullable().optional(),
  // V157: Establishment-level defaults
  address: z.string().nullable().optional(),
  hide_dba_default: z.boolean().nullable().optional(),
  // SPEC-test-suite-honesty R5(b): both establishment SELECTs
  // (models/establishments.py, archived + non-archived branches) return
  // `is_archived`, but the schema dropped it, so an archived DBA's flag was
  // stripped before any consumer could branch on it. Lenient per the v0.14.3
  // method (the non-archived list always sends FALSE; the include_archived
  // variant sends the real value).
  is_archived: z.boolean().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type Establishment = z.infer<typeof establishmentSchema>;

export const establishmentCreateSchema = z.object({
  name: z.string().min(1, 'Name is required').max(200),
  legal_name: z.string().max(200).nullable().optional(),  // Phase 9.23a: Override organization legal_name
  is_default: z.boolean().default(false),
  // V157
  address: z.string().nullable().optional(),
  hide_dba_default: z.boolean().default(false).optional(),
});

export type EstablishmentCreate = z.infer<typeof establishmentCreateSchema>;

export const establishmentUpdateSchema = establishmentCreateSchema.partial();
export type EstablishmentUpdate = z.infer<typeof establishmentUpdateSchema>;

export const establishmentListResponseSchema = z.object({
  success: z.boolean(),
  establishments: z.array(establishmentSchema),
  count: z.number(),
  organization_id: z.string().uuid(),  // UUID
});

export type EstablishmentListResponse = z.infer<typeof establishmentListResponseSchema>;

// ============================================================================
// Accounting Contacts (V100 migration — invoice billing contacts)
// ============================================================================

export const accountingContactSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  establishment_id: z.number().nullable().optional(),
  name: z.string().min(1).max(200),
  title: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  emails: z.array(z.string()),
  source: z.enum(['establishment', 'organization']).optional(),
  // V239 (SPEC-christa-contacts-identity R1): a client default contact keeps its TO/CC role and its place in
  // the list. NULL role = written by code older than V239 (the server then uses the first-is-TO rule).
  accounting_role: z.enum(['to', 'cc']).nullable().optional(),
  accounting_position: z.number().int().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type AccountingContact = z.infer<typeof accountingContactSchema>;

export const accountingContactCreateSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  title: z.string().nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
  emails: z.array(z.string().email('Invalid email')).min(1, 'At least one email is required'),
});

export type AccountingContactCreate = z.infer<typeof accountingContactCreateSchema>;

export const accountingContactUpdateSchema = accountingContactCreateSchema.partial();
export type AccountingContactUpdate = z.infer<typeof accountingContactUpdateSchema>;

export const accountingContactListResponseSchema = z.object({
  success: z.boolean(),
  contacts: z.array(accountingContactSchema),
});

export type AccountingContactListResponse = z.infer<typeof accountingContactListResponseSchema>;

export const accountingContactResponseSchema = z.object({
  success: z.boolean(),
  contact: accountingContactSchema,
});

export type AccountingContactResponse = z.infer<typeof accountingContactResponseSchema>;

/** PATCH .../accounting-contacts/{id}/role-order: the whole group in saved order. */
export const accountingContactRoleOrderResponseSchema = z.object({
  success: z.boolean(),
  establishment_id: z.number().nullable().optional(),
  contacts: z.array(z.object({
    contact_id: z.string(),
    name: z.string(),
    email: z.string().nullable().optional(),
    accounting_role: z.enum(['to', 'cc']).nullable().optional(),
    accounting_position: z.number().nullable().optional(),
  })),
});

export type AccountingContactRoleOrderResponse = z.infer<typeof accountingContactRoleOrderResponseSchema>;

/** GET /api/organizations/{id}/project-recipient-sets: projects of this client that have their own saved contacts. */
export const projectRecipientSetsResponseSchema = z.object({
  success: z.boolean(),
  projects: z.array(z.object({
    project_id: z.string(),
    client_scope_id: z.string(),
    project_number: z.string().nullable().optional(),
    project_name: z.string().nullable().optional(),
    names: z.array(z.string()),
  })),
});

export type ProjectRecipientSetsResponse = z.infer<typeof projectRecipientSetsResponseSchema>;

/** GET /api/projects/{id}/invoice-recipient-preview: who a NEW invoice would go to, and where from. */
export const invoiceRecipientPreviewSchema = z.object({
  source: z.enum(['project', 'project_cleared', 'client_default', 'last_sent', 'none']),
  recipients: z.array(z.object({
    contact_id: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    role: z.enum(['to', 'cc']),
    position: z.number(),
  })),
});

export type InvoiceRecipientPreview = z.infer<typeof invoiceRecipientPreviewSchema>;

// ============================================================================
// Project Scopes (Phase 5 - Organization Relationships)
// ============================================================================

// Phase 9.7bc: Scope contact from junction table
export const scopeContactSchema = z.object({
  id: z.string().uuid(),
  project_scope_id: z.string().uuid(),
  contact_id: z.string().uuid(),
  contact_name: z.string().nullable().optional(),
  contact_email: z.string().nullable().optional(),
  contact_phone: z.string().nullable().optional(),
  is_primary_contact: z.boolean().nullable().optional(),
  is_invoice_contact: z.boolean().nullable().optional(),
});

export type ScopeContact = z.infer<typeof scopeContactSchema>;

export const projectScopeSchema = z.object({
  id: z.string().uuid(),
  project_id: z.string().uuid(),
  organization_id: z.string().uuid().nullable().optional(), // Batch 7.3.4: Nullable for templates
  organization_name: z.string().nullable().optional(), // Batch 7.3.4: Nullable for templates
  scope_type_id: z.string().uuid(), // Migration 022: FK to tenant_scope_types.id
  scope_type_name: z.string().nullable().optional(), // Display name from tenant_scope_types (e.g., 'Client', 'Structural Engineer')
  scope_type_slug: z.string().nullable().optional(), // Slug from tenant_scope_types (e.g., 'client', 'self', 'structural_engineer')
  is_client_type: z.boolean().nullable().optional(), // Whether this scope type represents a client (from tenant_scope_types)
  is_primary: z.boolean().nullable().optional(), // Phase 9.7bc: Whether this is the primary client scope
  establishment_id: z.number().int().nullable().optional(), // Phase 9.28: Optional establishment (DBA) for client scopes
  establishment_name: z.string().nullable().optional(), // Phase 9.28: Establishment name from organization_establishments
  establishment_legal_name: z.string().nullable().optional(), // Phase 9.28: Legal name for invoicing
  contact_id: z.string().uuid().nullable().optional(),
  contact_name: z.string().nullable().optional(), // Contact name from organization_contacts
  contacts: z.array(scopeContactSchema).optional(), // Phase 9.7bc: Multiple contacts per scope
  status: z.string().nullable().optional(),
  status_notes: z.string().nullable().optional(),
  status_updated_at: z.string().nullable().optional(), // When status was last updated
  // R5: Latest note fields (from lateral subquery in get_project_scopes)
  latest_note_text: z.string().nullable().optional(),
  latest_note_created_by: z.string().nullable().optional(),
  latest_note_created_at: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type ProjectScope = z.infer<typeof projectScopeSchema>;

export const projectScopeListResponseSchema = z.object({
  success: z.boolean(),
  scopes: z.array(projectScopeSchema),
  total: z.number(),
  project_id: z.string().uuid(),
});

export type ProjectScopeListResponse = z.infer<typeof projectScopeListResponseSchema>;

// ============================================================================
// Project Scope Summary (R5 v0.14: BillingTracksPanel)
// ============================================================================

/**
 * Per-scope invoice aggregates returned by
 * GET /api/projects/{id}/scope-summary. Drives the BillingTracksPanel on the
 * project detail page — one row per project_scope with org/DBA names + invoice
 * counts. `total_invoice_count === 0` gates the [Remove] affordance for
 * never-used tracks.
 */
export const projectScopeSummarySchema = z.object({
  scope_id: z.string(),
  organization_id: z.string().nullable().optional(),
  organization_name: z.string().nullable().optional(),
  establishment_id: z.number().int().nullable().optional(),
  establishment_name: z.string().nullable().optional(),
  is_primary: z.boolean(),
  last_invoiced_at: z.string().nullable().optional(), // ISO date or null
  total_invoice_count: z.number().int(),
  sent_invoice_count: z.number().int(),
  // v0.14 B1 (§8.2 / §0.1): when an invoice has been org-changed AWAY from
  // this scope (the orphan-from-change case), these surface the lineage:
  // "Last invoice moved to {client} on {date}" instead of misleading
  // "Never used". NULL on genuinely-never-used scopes.
  last_invoice_moved_to_client_name: z.string().nullable().optional(),
  last_invoice_moved_at: z.string().nullable().optional(), // ISO timestamp
});

export type ProjectScopeSummary = z.infer<typeof projectScopeSummarySchema>;

export const projectScopeSummaryResponseSchema = z.object({
  success: z.boolean(),
  project_id: z.string(),
  scopes: z.array(projectScopeSummarySchema),
  total: z.number().int(),
});

export type ProjectScopeSummaryResponse = z.infer<typeof projectScopeSummaryResponseSchema>;

// ============================================================================
// Scope Notes (R5: Timestamped Notes Per Scope)
// ============================================================================

export const scopeNoteSchema = z.object({
  id: z.string().uuid(),
  project_scope_id: z.string().uuid(),
  tenant_id: z.string(),
  note_text: z.string(),
  created_by: z.string(),
  created_at: z.string(),
});

export type ScopeNote = z.infer<typeof scopeNoteSchema>;

// ============================================================================
// Scope Types (Phase 5 Batch 3 - Tenant-configurable scope types)
// ============================================================================

export const scopeTypeSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string(),
  name: z.string(), // Slug: 'client', 'structural_engineer'
  display_name: z.string(), // Display: 'Client', 'Structural Engineer'
  is_client_type: z.boolean(),
  display_order: z.number().int(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type ScopeType = z.infer<typeof scopeTypeSchema>;

export const scopeTypeCreateSchema = z.object({
  name: z.string().min(1).max(100),
  display_name: z.string().min(1).max(200),
  is_client_type: z.boolean().default(false),
  display_order: z.number().int().default(0),
});

export type ScopeTypeCreate = z.infer<typeof scopeTypeCreateSchema>;

export const scopeTypeUpdateSchema = scopeTypeCreateSchema.partial();

export type ScopeTypeUpdate = z.infer<typeof scopeTypeUpdateSchema>;

export const scopeTypeListResponseSchema = z.object({
  success: z.boolean(),
  scope_types: z.array(scopeTypeSchema),
  total: z.number(),
});

export type ScopeTypeListResponse = z.infer<typeof scopeTypeListResponseSchema>;

// ============================================================================
// Organization Categories (Phase 6 Batch 2 - Tenant Configurable)
// ============================================================================

export const organizationCategorySchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string(),
  name: z.string(), // Slug: 'structural_engineer', 'mechanical_engineer'
  display_name: z.string(), // Display: 'Structural Engineer', 'Mechanical Engineer'
  display_order: z.number().int(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type OrganizationCategory = z.infer<typeof organizationCategorySchema>;

export const organizationCategoryCreateSchema = z.object({
  display_name: z.string().min(1).max(50),
  name: z.string().min(1).max(50).optional(), // Optional: auto-generated from display_name if not provided
  display_order: z.number().int().optional(), // Optional: auto-assigned if not provided
});

export type OrganizationCategoryCreate = z.infer<typeof organizationCategoryCreateSchema>;

export const organizationCategoryUpdateSchema = organizationCategoryCreateSchema.partial();

export type OrganizationCategoryUpdate = z.infer<typeof organizationCategoryUpdateSchema>;

export const organizationCategoryListResponseSchema = z.object({
  success: z.boolean(),
  categories: z.array(organizationCategorySchema),
  total: z.number(),
});

export type OrganizationCategoryListResponse = z.infer<typeof organizationCategoryListResponseSchema>;

// ============================================================================
// Permits (Phase 6 Batch 2)
// ============================================================================

// QW-5: Added 'Time Limited Development Permit' (Christa feedback 2024-12-22)
export const permitTypeSchema = z.enum([
  'Building Permit',
  'Development Permit',
  'DB Permit',
  'Occupant Load',
  'Private Patio DP',
  'Time Limited Development Permit',
  'Inquiry',
  'Alternate Solution',
  'Awning',
  'Signage',
  'Other'
]);

export type PermitType = z.infer<typeof permitTypeSchema>;

export const permitStatusSchema = z.enum(['not_submitted', 'submitted', 'issued', 'closed']);

export type PermitStatus = z.infer<typeof permitStatusSchema>;

export const permitSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string(),
  project_id: z.string().uuid(),
  permit_type: permitTypeSchema,
  case_number: z.string().nullable().optional(),
  permit_number: z.string().nullable().optional(),
  reviewer_name: z.string().nullable().optional(),
  date_submitted: z.string().nullable().optional(), // ISO date string
  date_issued: z.string().nullable().optional(),
  date_closed: z.string().nullable().optional(),

  // V008 fields - Phase 6 Batch 2
  due_date: z.string().nullable().optional(), // ISO date string - expiration/renewal date
  recurring_period: z.string().nullable().optional(), // e.g., "Annual", "Every 2 years"
  fees_costs: z.number().nullable().optional(), // Permit fees in dollars

  notes: z.string().nullable().optional(),
  status: permitStatusSchema, // Calculated from dates
  created_at: z.string(),
  updated_at: z.string(),
});

export type Permit = z.infer<typeof permitSchema>;

export const permitCreateSchema = z.object({
  permit_type: permitTypeSchema,
  case_number: z.string().optional(),
  permit_number: z.string().optional(),
  reviewer_name: z.string().optional(),
  date_submitted: z.string().optional(),
  date_issued: z.string().optional(),
  date_closed: z.string().optional(),
  due_date: z.string().optional(),
  recurring_period: z.string().optional(),
  fees_costs: z.number().min(0).optional(),
  notes: z.string().optional(),
});

export type PermitCreate = z.infer<typeof permitCreateSchema>;

export const permitUpdateSchema = permitCreateSchema.partial();

export type PermitUpdate = z.infer<typeof permitUpdateSchema>;

export const permitListResponseSchema = z.object({
  success: z.boolean(),
  permits: z.array(permitSchema),
  total: z.number(),
  project_id: z.string(),
});

export type PermitListResponse = z.infer<typeof permitListResponseSchema>;

export const permitResponseSchema = z.object({
  success: z.boolean(),
  permit: permitSchema,
  timestamp: z.string().optional(),
});

export type PermitResponse = z.infer<typeof permitResponseSchema>;

// ============================================================================
// Settings (Phase 6 - Settings Page)
// ============================================================================

export const organizationSettingsSchema = z.object({
  legal_name: z.string().min(1).max(200),
  dba_name: z.string().max(200).nullable().optional(),
  business_registration_number: z.string().max(100).nullable().optional(),
  tax_id_number: z.string().max(50).nullable().optional(),
  address: z.string().max(500).nullable().optional(),
  phone: z.string().max(20).nullable().optional(),
  email: z.string().nullable().optional(), // Accept any string from API (validation happens on input)
  // R5 (SPEC-tenant-identity-settings-2026-08-17): OPTIONAL second address --
  // where PAYMENT is sent, distinct from `address` above. Unset (null) is the
  // common case and renders no extra block on the invoice.
  remit_to_address: z.string().max(500).nullable().optional(),
});

export type OrganizationSettings = z.infer<typeof organizationSettingsSchema>;

export const invoiceDefaultsSchema = z.object({
  payment_terms: z.number().int().min(0).max(365),
  tax_rate: z.number().min(0).max(100),
  tax_label: z.string().min(1).max(50),
  default_notes: z.string().max(2000).nullable().optional(),
  terms_conditions: z.string().max(2000).nullable().optional(),
  payment_instructions: z.string().max(2000).nullable().optional(),
});

export type InvoiceDefaults = z.infer<typeof invoiceDefaultsSchema>;

export const brandingSettingsSchema = z.object({
  logo_svg: z.string().nullable().optional(),
  invoice_logo_svg: z.string().nullable().optional(), // Phase 9.18a: Separate logo for invoices
  primary_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color'),
  cover_image_base64: z.string().nullable().optional(), // Phase 10c.9: Default proposal cover image
  signature_image_base64: z.string().nullable().optional(), // Phase 10c.9: Signature image
});

export type BrandingSettings = z.infer<typeof brandingSettingsSchema>;

// ProposalDefaults (Phase 8.3 - Proposal PDF Generation)
export const taxDisplayModeSchema = z.enum(['none', 'label', 'calculated']);
export type TaxDisplayMode = z.infer<typeof taxDisplayModeSchema>;

export const proposalDefaultsSchema = z.object({
  tax_display_mode: taxDisplayModeSchema,
  payment_terms: z.array(z.string()),
  validity_days: z.number().int().min(1).max(365),
  // 9.9c: Custom document prefix (e.g., "Banks" -> "Banks Fee Letter")
  document_prefix: z.string().optional(),
});

export type ProposalDefaults = z.infer<typeof proposalDefaultsSchema>;

// SiteVisitDefaults — configurable field review settings
export const checklistStatusOptionSchema = z.object({
  value: z.string().min(1).max(50),
  label: z.string().min(1).max(100),
});

export type ChecklistStatusOption = z.infer<typeof checklistStatusOptionSchema>;

export const siteVisitDefaultsSchema = z.object({
  checklist_statuses: z.array(checklistStatusOptionSchema).default([
    { value: 'info_only', label: 'Info Only' },
    { value: 'contractor_action', label: 'Contractor Action' },
    { value: 'architect_action', label: 'Architect Action' },
  ]),
});

export type SiteVisitDefaults = z.infer<typeof siteVisitDefaultsSchema>;

// EmailSettings (Phase 9.1a - Email Integration)
export const emailSignatureSchema = z.object({
  name: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  company: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
});

export type EmailSignature = z.infer<typeof emailSignatureSchema>;

export const emailSettingsSchema = z.object({
  reply_to: z.string().nullable().optional(),
  bcc_list: z.array(z.string()).default([]),
  enabled: z.boolean().default(false),
  signature: emailSignatureSchema.nullable().optional(),
});

export type EmailSettings = z.infer<typeof emailSettingsSchema>;

// Financial Settings (Phase 9.5b - Revenue Goal Widget, Phase 10a.5 - Fiscal Year)
export const financialSettingsSchema = z.object({
  annual_revenue_goal: z.number().nonnegative().default(0),
  fiscal_year_start_month: z.number().int().min(1).max(12).default(1),
});

export type FinancialSettings = z.infer<typeof financialSettingsSchema>;

// TaxSettings (Dashboard widgets: GST remittance + corp tax estimate)
export const taxSettingsSchema = z.object({
  gst_remittance_period: z.enum(['quarterly', 'annual']).default('quarterly'),
  gst_reminder_days: z.number().int().min(1).max(90).default(14),
  corp_tax_rate: z.number().min(0).max(100).default(0),
  corp_tax_remittance_dates: z.array(z.string()).default([]),
});

export type TaxSettings = z.infer<typeof taxSettingsSchema>;

// TaxEngineStatus (v0.17 taxclass-label fix, SPEC-bc-pst-tax-engine-2026-08-04)
// GET /api/me/settings/tax-engine — read-only mode + rule enablement so the
// tax-class labels (lib/taxClasses.ts) can stop claiming PST applies when the
// tenant is still on 'legacy' or the BC_PST rule is disabled/not yet effective.
export const taxRuleStatusSchema = z.object({
  code: z.string(),
  enabled: z.boolean(),
  effective_from: z.string().nullable().optional(),
});

export type TaxRuleStatus = z.infer<typeof taxRuleStatusSchema>;

export const taxEngineStatusSchema = z.object({
  success: z.boolean(),
  tax_engine: z.enum(['legacy', 'rules']).default('legacy'),
  rules: z.array(taxRuleStatusSchema).default([]),
});

export type TaxEngineStatus = z.infer<typeof taxEngineStatusSchema>;

export const settingsResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
});

export type SettingsResponse = z.infer<typeof settingsResponseSchema>;

// OperationalSettings (B4 - Dynamic overdue threshold)
export const operationalSettingsSchema = z.object({
  acceptance_reminder_hours: z.number().int().min(1).max(8760),
});

export type OperationalSettings = z.infer<typeof operationalSettingsSchema>;

// ============================================================================
// Project Fee Assumptions (9.9d CONTRACT-03)
// ============================================================================

export const projectFeeAssumptionSchema = z.object({
  id: z.string().uuid(),
  project_id: z.string().uuid(),
  tenant_id: z.string(),
  assumption_text: z.string().min(1).max(2000),
  display_order: z.number().int().nonnegative(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type ProjectFeeAssumption = z.infer<typeof projectFeeAssumptionSchema>;

export const projectFeeAssumptionCreateSchema = z.object({
  assumption_text: z.string().min(1, "Assumption text is required").max(2000),
  display_order: z.number().int().nonnegative().optional(),
});

export type ProjectFeeAssumptionCreate = z.infer<typeof projectFeeAssumptionCreateSchema>;

export const projectFeeAssumptionUpdateSchema = projectFeeAssumptionCreateSchema.partial();

export type ProjectFeeAssumptionUpdate = z.infer<typeof projectFeeAssumptionUpdateSchema>;

export const projectFeeAssumptionListResponseSchema = z.object({
  success: z.boolean(),
  fee_assumptions: z.array(projectFeeAssumptionSchema),
  total: z.number(),
});

export type ProjectFeeAssumptionListResponse = z.infer<typeof projectFeeAssumptionListResponseSchema>;

export const projectFeeAssumptionResponseSchema = z.object({
  success: z.boolean(),
  fee_assumption: projectFeeAssumptionSchema,
});

export type ProjectFeeAssumptionResponse = z.infer<typeof projectFeeAssumptionResponseSchema>;

export const projectFeeAssumptionReorderItemSchema = z.object({
  id: z.string().uuid(),
  display_order: z.number().int().nonnegative(),
});

export type ProjectFeeAssumptionReorderItem = z.infer<typeof projectFeeAssumptionReorderItemSchema>;

// ============================================================================
// User Views (Phase 10a.9)
// ============================================================================

export const userViewConfigSchema = z.object({
  filters: z.record(z.string(), z.unknown()).optional(),
  columns: z.array(z.string()).optional(),
  sort: z.object({
    field: z.string(),
    direction: z.enum(['asc', 'desc']),
  }).optional(),
  quick_actions: z.array(z.string()).optional(),
  highlight_rules: z.array(z.any()).optional(),
});

export type UserViewConfig = z.infer<typeof userViewConfigSchema>;

export const userViewSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string(),
  user_id: z.string().nullable(),
  name: z.string(),
  view_type: z.string(),
  config: userViewConfigSchema,
  icon: z.string(),
  position: z.number(),
  pinned: z.boolean(),
  created_by: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type UserView = z.infer<typeof userViewSchema>;

export const userViewListResponseSchema = z.object({
  success: z.boolean(),
  views: z.array(userViewSchema),
  total: z.number(),
  tenant_id: z.string(),
});

export type UserViewListResponse = z.infer<typeof userViewListResponseSchema>;

// ============================================================================
// Default Currency Settings (Phase 10a.9)
// ============================================================================

export const defaultCurrencySettingsSchema = z.object({
  default_currency: z.enum(['CAD', 'USD']).default('CAD'),
});

export type DefaultCurrencySettings = z.infer<typeof defaultCurrencySettingsSchema>;

// ============================================================================
// Proposal Config (Phase 10c.1 - Proposal terminology customization)
// ============================================================================

export const proposalSectionNamesSchema = z.object({
  understanding: z.string().default('Understanding of Scope'),
  fee_structure: z.string().default('Fee Structure'),
  assumptions: z.string().default('Assumptions & Inclusions'),
  inclusions: z.string().default('Inclusions'),
  exclusions: z.string().default('Exclusions'),
});

export const proposalItemLabelsSchema = z.object({
  item_description: z.string().default('Description'),
  unit_label: z.string().default('Unit'),
  rate_label: z.string().default('Rate'),
  amount_label: z.string().default('Amount'),
});

export const proposalConfigSchema = z.object({
  document_title: z.string().default('Proposal'),
  section_names: proposalSectionNamesSchema.default({}),
  item_labels: proposalItemLabelsSchema.default({}),
});

export type ProposalSectionNames = z.infer<typeof proposalSectionNamesSchema>;
export type ProposalItemLabels = z.infer<typeof proposalItemLabelsSchema>;
export type ProposalConfig = z.infer<typeof proposalConfigSchema>;

// ============================================================================
// API Error Response
// ============================================================================

export const apiErrorSchema = z.object({
  success: z.literal(false),
  error: z.string(),
  message: z.string().optional(),
  suggestion: z.unknown().optional(), // For 409 conflicts with retry suggestions
});

export type ApiError = z.infer<typeof apiErrorSchema>;

// ============================================================================
// Onboarding — Phase 12
// ============================================================================

export interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  action_url: string;
  completed: boolean;
  completed_at: string | null;
}

export interface OnboardingStatusResponse {
  success: boolean;
  firm_type: string | null;
  onboarding_status: 'pending' | 'in_progress' | 'completed' | 'skipped';
  onboarding_completed_at: string | null;
  module_visibility: Record<string, boolean> | null;
  checklist: {
    checklist_type: string | null;
    items: ChecklistItem[];
  } | null;
}

export interface OnboardingSetupRequest {
  firm_type: string;
  company_name?: string;
  logo_url?: string;
  address?: string;
  fiscal_year_start_month?: number;
}

// ============================================================================
// Stakeholder Portal — Report Feedback (SPEC-stakeholder-portal-2026-06-23, R3)
// ============================================================================
//
// These schemas mirror the backend privacy read's EXACT external shape
// (models/stakeholder_portal.TENANT_FEEDBACK_ROW_KEYS). EVERY field the backend
// SELECT returns is declared here, so a successful Zod parse strips nothing and
// no [Zod-strip] warning fires. The privacy read NEVER returns internal-only
// fields (priority, planned_release, internal title, visibility, milestone,
// github_issue_number, reporter identities, or other tenants' data) — so this
// schema deliberately has no way to surface them either.

// The derived claim-ladder rung (a pure function of the work_item's three status
// axes on the backend — never a stored field). For an untriaged report it is the
// fixed 'received' rung.
export const feedbackClaimLadderSchema = z.object({
  key: z.string(),
  emoji: z.string(),
  label: z.string(),
});
export type FeedbackClaimLadder = z.infer<typeof feedbackClaimLadderSchema>;

// R5 AC1: one entry in a report's privacy-safe status TIMELINE. Built on the
// backend by replaying the work_item's client-safe transition log into 3-axis
// snapshots, deriving a rung per snapshot, and collapsing consecutive identical
// rungs. `relative_time` is a PAST-ONLY plain-language string (never an absolute
// or future date); the entry NEVER carries `actor` or a raw internal axis state.
export const feedbackTimelineEntrySchema = z.object({
  key: z.string(),
  emoji: z.string(),
  label: z.string(),
  relative_time: z.string(),
});
export type FeedbackTimelineEntry = z.infer<typeof feedbackTimelineEntrySchema>;

// R6 AC5: one client "add a note" echo. The reporter's own note, lifted out of the
// durable body-append markers on the backend so the row can reflect their words back
// ("You: <text> · <relative time>"). `created_at` is an ISO timestamp rendered as a
// PAST relative string on the client — never an internal field, never another
// reporter's note.
export const feedbackNoteSchema = z.object({
  text: z.string(),
  created_at: z.string(),
});
export type FeedbackNote = z.infer<typeof feedbackNoteSchema>;

// R5 (upload-hardening + multi-screenshot): one attached image's SHORT-TTL
// signed URL + its 0-based position (the PRIMARY image is position 0, also
// dual-written to the legacy screenshot_url field for back-compat). The raw
// storage ref is NEVER exposed -- only this per-read minted signed URL.
export const feedbackImageSchema = z.object({
  url: z.string(),
  position: z.number(),
});
export type FeedbackImage = z.infer<typeof feedbackImageSchema>;

// One "Your Issues" row. Linked-work_item status fields are null while untriaged.
export const feedbackItemSchema = z.object({
  feedback_id: z.string(),
  body: z.string(),
  source_screen: z.string().nullable(),
  route: z.string().nullable(),
  created_at: z.string().nullable(),
  acceptance_state: z.string(),
  work_item_id: z.string().nullable(),
  claim_ladder: feedbackClaimLadderSchema,
  // R5 AC6: the raw `stage` field was DROPPED from the client surface — it was an
  // internal axis value folded into claim_ladder and unused by the render. It is
  // deliberately absent here so the shape-lock freezes the reduced surface.
  addressed_in_build: z.string().nullable(),
  horizon: z.string().nullable(),
  resolution_note: z.string().nullable(),
  // R4: a SHORT-TTL signed URL to the reporter's OWN attached image (null when
  // there is none). The backend NEVER exposes the raw storage ref (screenshot_ref)
  // — only this per-read minted signed URL, scoped to the caller's own row.
  screenshot_url: z.string().nullable(),
  // A SHORT-TTL signed URL to the reporter's OWN "still broken" regression image
  // (null when none). Separate from screenshot_url so the ORIGINAL report image is
  // preserved alongside the fresh regression evidence. Raw ref never exposed.
  regression_screenshot_url: z.string().nullable(),
  // R5 AC1: the collapsed public status timeline ([] for an untriaged report).
  timeline: z.array(feedbackTimelineEntrySchema),
  // R5 AC3: the most-recent activity timestamp (ISO). Rendered as a PAST relative
  // string / stale reassurance — never as an absolute or future date.
  last_updated: z.string().nullable(),
  // R6 AC5: the reporter's OWN "add a note" echoes (oldest-first), lifted out of
  // the body-append markers on the backend. [] when the reporter has added none.
  notes: z.array(feedbackNoteSchema),
  // R3 (tabbed panel shell) AC3: whether this row's client-visible status
  // changed since the caller's last-seen marker — drives the My Submissions
  // panel tab's unseen-before-seen ordering. OPTIONAL: only the reporter-
  // scoped `/issues` (panel) read populates it; the create-receipt and other
  // feedbackItemSchema call sites simply omit it (undefined, never a
  // [Zod-strip] — the field is absent from those payloads, not stripped).
  is_unseen: z.boolean().optional(),
  // R5 (upload-hardening + multi-screenshot, HIGH-3 fix): up to 5 attached
  // images, own images only, position-ordered. [] when none. Was previously
  // missing from this schema entirely, silently [Zod-strip]ping the backend's
  // `images` field on every read.
  images: z.array(feedbackImageSchema).default([]),
  // SPEC-feedback-return-path-2026-08-17 R4 (AC1/AC5/AC9): the two-way
  // question/answer pair, present regardless of triage state -- these are
  // plain `feedback` columns (never folded from work_item), so an untriaged
  // row carries them too. null when no question has been asked. OPTIONAL
  // (mirrors is_unseen above): the backend always sends these on every real
  // read, but declaring them optional (rather than required) keeps every
  // pre-existing feedbackItemSchema-shaped test fixture across this codebase
  // valid without a mechanical edit to each one -- an absent key here reads
  // identically to null in every render path below (no question asked).
  operator_question: z.string().nullable().optional(),
  operator_question_asked_at: z.string().nullable().optional(),
  reporter_answer: z.string().nullable().optional(),
  reporter_answered_at: z.string().nullable().optional(),
});
export type FeedbackItem = z.infer<typeof feedbackItemSchema>;

// POST /api/feedback response: the durable receipt (a single item, same shape)
// plus the R5 AC6 per-image outcome signal.
export const createFeedbackResponseSchema = z.object({
  feedback: feedbackItemSchema,
  images_attached: z.number().default(0),
  images_failed: z.number().default(0),
});
export type CreateFeedbackResponse = z.infer<typeof createFeedbackResponseSchema>;

// GET /api/feedback response: the caller's own "Your Issues" list.
export const feedbackListResponseSchema = z.object({
  feedback: z.array(feedbackItemSchema),
});
export type FeedbackListResponse = z.infer<typeof feedbackListResponseSchema>;

export interface CreateFeedbackRequest {
  body: string;
  source_screen?: string | null;
  route?: string | null;
  // R5 (HIGH-3 fix): up to 5 images attached to the report (multipart, field
  // name `images` — matches routers/feedback.py's MAX_IMAGES_PER_REPORT).
  // Omitted/empty when the reporter attaches nothing (the request is then a
  // plain text report). Was a single `image?: File | null` before R5 — the
  // backend supported up to 5 images from AC4 onward, but no client could
  // ever exercise more than one.
  images?: File[];
  // Finding 5: a client-generated idempotency key, one per report dialog session and
  // stable across a double-fire, so a concurrent double-submit collapses to ONE row
  // at the DB (V193 partial unique index). Optional -- a keyless request is unchanged.
  submit_key?: string;
}

// ── R5 stakeholder portal: the date-free Now/Next/Later roadmap ────────────────
// One curated roadmap item. Comes from the backend's ROADMAP_ITEM_KEYS allow-list
// (WHERE visibility='stakeholder') — it deliberately carries NO priority, NO
// planned_release dates, NO milestone, NO internal-only fields, and no raw
// "stage" jargon (folded into claim_ladder + the in_flight boolean). `in_flight`
// is null at the `roadmap` level and a boolean at `planned`. Every backend field
// is declared here (no [Zod-strip] dead field).
export const roadmapItemSchema = z.object({
  work_item_id: z.string(),
  title: z.string().nullable(),
  horizon: z.string().nullable(),
  claim_ladder: feedbackClaimLadderSchema,
  in_flight: z.boolean().nullable(),
  addressed_in_build: z.string().nullable(),
});
export type RoadmapItem = z.infer<typeof roadmapItemSchema>;

export const roadmapResponseSchema = z.object({
  visibility_level: z.string(),
  roadmap_visible: z.boolean(),
  planned_detail_visible: z.boolean(),
  items: z.array(roadmapItemSchema),
});
export type RoadmapResponse = z.infer<typeof roadmapResponseSchema>;

// ── R4 (roadmap + all-issues): the client HYPE roadmap ─────────────────────
// GET /api/stakeholder-portal/epic-roadmap (R1 AC3) — top-level EPICS only,
// allow-listed to EPIC_PUBLIC_KEYS. Structurally distinct from roadmapItemSchema
// above (the pre-epic all-visible-items board): NO in_flight / addressed_in_build
// (no "planned detail" tier on the hype face), and `claim_ladder` is present on
// the wire but the RoadmapTab component deliberately never renders it — R4 AC1
// requires the client roadmap to show a headline + description + a plain
// "now/coming soon/later" framing, NEVER the internal claim-ladder/StatusPill
// vocabulary. Every backend field is declared here (no [Zod-strip] dead field).
export const epicRoadmapItemSchema = z.object({
  work_item_id: z.string(),
  claim_ladder: feedbackClaimLadderSchema,
  horizon: z.string().nullable(),
  title: z.string().nullable(),
  description: z.string().nullable(),
});
export type EpicRoadmapItem = z.infer<typeof epicRoadmapItemSchema>;

export const epicRoadmapResponseSchema = z.object({
  visibility_level: z.string(),
  roadmap_visible: z.boolean(),
  items: z.array(epicRoadmapItemSchema),
});
export type EpicRoadmapResponse = z.infer<typeof epicRoadmapResponseSchema>;

// The accept / still-broken write-back responses (portal accept loop). Declared
// with every field the backend returns so the Zod boundary keeps them.
// F2/B2: `human_resolution` (the RAW global coordinator axis) is deliberately NOT
// declared — the backend no longer emits it on these client write-backs (privacy),
// and the page re-renders the DERIVED claim_ladder from a /issues refetch, never
// the raw axis. Declaring it would silently re-admit the leak into the client type.
export const portalAcceptResponseSchema = z.object({
  feedback_id: z.string(),
  acceptance_state: z.string(),
  should_close: z.boolean().nullable().optional(),
  github_synced: z.boolean().nullable().optional(),
  github_action: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
});
export type PortalAcceptResponse = z.infer<typeof portalAcceptResponseSchema>;

// The un-accept (durable undo) write-back response (R1 AC3). A tri-state undo:
// reverted=true (flipped accepted→pending + reopened), reverted=false/refused=false
// (idempotent no-op), or refused=true (coordinator-terminal — nothing written).
// Every backend field is declared so the Zod boundary keeps them (no [Zod-strip]).
export const portalUnAcceptResponseSchema = z.object({
  feedback_id: z.string(),
  acceptance_state: z.string(),
  reverted: z.boolean(),
  refused: z.boolean().optional(),
  reason: z.string().nullable().optional(),
  // F2/B2: raw human_resolution axis intentionally NOT declared (see accept schema).
  should_close: z.boolean().nullable().optional(),
  github_synced: z.boolean().nullable().optional(),
  github_action: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
});
export type PortalUnAcceptResponse = z.infer<typeof portalUnAcceptResponseSchema>;

// R6 AC1: the withdraw ("No longer needed") write-back response. Client-axis only;
// idempotent (idempotent_noop=true when already withdrawn). should_close/github_*
// are null on an untriaged report. Every backend field is declared (no [Zod-strip]).
export const portalWithdrawResponseSchema = z.object({
  feedback_id: z.string(),
  acceptance_state: z.string(),
  withdrawn: z.boolean(),
  idempotent_noop: z.boolean().optional(),
  fanout_fired: z.boolean().optional(),
  should_close: z.boolean().nullable().optional(),
  github_synced: z.boolean().nullable().optional(),
  github_action: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
});
export type PortalWithdrawResponse = z.infer<typeof portalWithdrawResponseSchema>;

// R6 AC2: the un-withdraw ("changed my mind") write-back response. A safe two-state
// undo: reverted=true (withdrawn→pending + reopened) or reverted=false (idempotent
// no-op, reason='not_withdrawn'). Every backend field declared (no [Zod-strip]).
export const portalUnWithdrawResponseSchema = z.object({
  feedback_id: z.string(),
  acceptance_state: z.string(),
  reverted: z.boolean(),
  reason: z.string().nullable().optional(),
  fanout_fired: z.boolean().optional(),
  should_close: z.boolean().nullable().optional(),
  github_synced: z.boolean().nullable().optional(),
  github_action: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
});
export type PortalUnWithdrawResponse = z.infer<typeof portalUnWithdrawResponseSchema>;

// R6 AC4/AC5: the add-a-note write-back response. Returns the reporter's OWN notes
// echoed back ({text, created_at}) so their words render on their row.
export const portalAddNoteResponseSchema = z.object({
  feedback_id: z.string(),
  notes: z.array(feedbackNoteSchema),
});
export type PortalAddNoteResponse = z.infer<typeof portalAddNoteResponseSchema>;

// SPEC-feedback-return-path-2026-08-17 R4 (AC6/AC8): POST
// /api/stakeholder-portal/feedback/{id}/answer — the reporter's answer to an
// operator's question. Every backend field declared (no [Zod-strip]).
export const portalAnswerQuestionResponseSchema = z.object({
  feedback_id: z.string(),
  reporter_answer: z.string(),
  reporter_answered_at: z.string().nullable().optional(),
});
export type PortalAnswerQuestionResponse = z.infer<typeof portalAnswerQuestionResponseSchema>;

// R2 (header entry + unseen badge): GET /api/stakeholder-portal/unseen-count.
// A single integer — the caller's OWN submissions whose client-visible status
// changed since their last-seen marker (server-authoritative; no [Zod-strip]).
export const portalUnseenCountResponseSchema = z.object({
  unseen_count: z.number(),
});
export type PortalUnseenCountResponse = z.infer<typeof portalUnseenCountResponseSchema>;

// R2: POST /api/stakeholder-portal/mark-seen. Returns the caller's last-seen
// marker AFTER the monotonic write (ISO timestamp) — always the winning,
// largest of the existing and incoming values.
export const portalMarkSeenResponseSchema = z.object({
  last_seen_at: z.string(),
});
export type PortalMarkSeenResponse = z.infer<typeof portalMarkSeenResponseSchema>;

// R3 (tabbed panel shell) AC5: GET /api/stakeholder-portal/panel-config. The
// capability-derived tab set — `tabs` carries only identifiers, never a
// coordination/all-issues DATA payload (that stays behind the pre-existing
// super-admin-gated /api/coordination endpoints).
export const portalPanelConfigResponseSchema = z.object({
  tabs: z.array(z.string()),
  is_super_admin: z.boolean(),
});
export type PortalPanelConfigResponse = z.infer<typeof portalPanelConfigResponseSchema>;

// ── R6 internal coordination view (super-admin, cross-tenant) ──────────────────
// One work_item on the INTERNAL board — this surface DELIBERATELY carries the
// internal-only fields (priority, planned_release dates, all affected tenants,
// linked-feedback count) that the external tenant portal (R5) never shows. It is
// reached only through the super-admin-gated /api/coordination endpoints (a
// DIRECT is_super_admin check at the backend function boundary + the isolated
// cross-tenant reader module). Every backend field is declared (no [Zod-strip]).
// The plain work_item store row (returned by the planning write) — the full
// internal column set, WITHOUT the board aggregates.
export const coordinationWorkItemRowSchema = z.object({
  id: z.string(),
  github_issue_number: z.number().nullable(),
  title: z.string().nullable(),
  stage: z.string().nullable(),
  system_verification: z.string().nullable(),
  human_resolution: z.string().nullable(),
  addressed_in_build: z.string().nullable(),
  milestone: z.string().nullable(),
  priority: z.string().nullable(),
  planned_release: z.string().nullable(),
  horizon: z.string().nullable(),
  visibility: z.string().nullable(),
  resolution_note: z.string().nullable(),
  // R1 (epics)/R4 AC6: is_epic / parent_epic_id / description (V202) now ride
  // on this internal row (models/coordination_view.py's shared column set).
  // Declared here (optional — not every mock/call site sends them yet) so the
  // Zod boundary keeps them instead of an unexplained [Zod-strip].
  is_epic: z.boolean().optional(),
  parent_epic_id: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});
export type CoordinationWorkItemRow = z.infer<typeof coordinationWorkItemRowSchema>;

// A board row = the store row + the cross-tenant aggregates (count + tenants).
export const coordinationWorkItemSchema = coordinationWorkItemRowSchema.extend({
  feedback_count: z.number(),
  tenants: z.array(z.string()),
  // R4 AC3/AC4: a DERIVED claim_ladder, attached by the router on the
  // all-issues + epic-drill-down reads (routers/coordination_view.py::
  // _with_claim_ladder) so those surfaces reuse the SAME StatusPill
  // rendering as the client portal. Optional — the plain /board read
  // (R3, unchanged) does not carry it.
  claim_ladder: feedbackClaimLadderSchema.optional(),
});
export type CoordinationWorkItem = z.infer<typeof coordinationWorkItemSchema>;

// One untriaged feedback row in the cross-tenant triage queue (internal-only).
export const coordinationUntriagedFeedbackSchema = z.object({
  id: z.string(),
  tenant_id: z.string().nullable(),
  reporter_user_id: z.string().nullable(),
  reporter_name: z.string().nullable(),
  source_screen: z.string().nullable(),
  route: z.string().nullable(),
  body: z.string(),
  created_at: z.string().nullable(),
  // R4: a SHORT-TTL signed URL to the reporter's attached image (null when none).
  // The router folds the raw screenshot_ref into this signed URL — the raw ref
  // never reaches the wire.
  screenshot_url: z.string().nullable(),
  // The still-broken regression image, same fold-to-signed-URL rule (null when none).
  regression_screenshot_url: z.string().nullable(),
  // SPEC-stakeholder-portal-presentation-redesign-2026-07-20 (cross-ring gap
  // close): the FULL multi-image list (up to 5, R5), position-ordered,
  // mirroring feedbackImageSchema — the same allow-listed fold-the-ref shape
  // as the tenant-facing read. [] when the report carries no images (or
  // predates R5, where only the legacy screenshot_url carries the image).
  images: z.array(feedbackImageSchema).default([]),
  // SPEC-feedback-return-path-2026-08-17 R4 (AC1): the two-way question/
  // answer pair, present on an untriaged row too (plain `feedback` columns).
  // Optional, mirroring feedbackItemSchema's own reasoning above.
  operator_question: z.string().nullable().optional(),
  operator_question_asked_at: z.string().nullable().optional(),
  reporter_answer: z.string().nullable().optional(),
  reporter_answered_at: z.string().nullable().optional(),
});
export type CoordinationUntriagedFeedback = z.infer<typeof coordinationUntriagedFeedbackSchema>;

export const coordinationBoardResponseSchema = z.object({
  work_items: z.array(coordinationWorkItemSchema),
  untriaged_feedback: z.array(coordinationUntriagedFeedbackSchema),
});
export type CoordinationBoardResponse = z.infer<typeof coordinationBoardResponseSchema>;

// GET /api/coordination/epics/{epic_id} (R1 AC4 / R4 AC4) — the super-admin
// epic drill-down: one epic + its direct sub-issues, each at the SAME
// coordinator-view fidelity as a board row (+ the router-attached
// claim_ladder, R4 AC3/AC4). Reuses coordinationWorkItemSchema rather than a
// parallel shape.
export const epicDrillDownResponseSchema = z.object({
  epic: coordinationWorkItemSchema,
  sub_issues: z.array(coordinationWorkItemSchema),
});
export type EpicDrillDownResponse = z.infer<typeof epicDrillDownResponseSchema>;

// One linked-feedback row in the coordinator DETAIL read behind a work_item's
// feedback COUNT (internal-only, super-admin, cross-tenant). Carries the reporter's
// regression NOTE (durably appended in `body`), the client-note echoes, and BOTH
// signed image URLs. The router folds the raw refs into these signed URLs — the raw
// screenshot_ref / regression_screenshot_ref never reach the wire.
export const coordinationLinkedFeedbackSchema = z.object({
  id: z.string(),
  tenant_id: z.string().nullable(),
  reporter_user_id: z.string().nullable(),
  reporter_name: z.string().nullable(),
  acceptance_state: z.string().nullable(),
  body: z.string().nullable(),
  // The reporter's own "[Note — …]" echoes, oldest-first ({text, created_at}).
  notes: z.array(z.object({ text: z.string(), created_at: z.string() })),
  // SHORT-TTL signed URLs (null when the reporter attached no image). The
  // regression image is the "still broken" evidence.
  screenshot_url: z.string().nullable(),
  regression_screenshot_url: z.string().nullable(),
  // SPEC-stakeholder-portal-presentation-redesign-2026-07-20 (cross-ring gap
  // close): the FULL multi-image list (up to 5, R5) attached to the ORIGINAL
  // report, position-ordered, same allow-listed fold-the-ref shape as the
  // tenant-facing read. [] when the report carries no images (or predates
  // R5). Distinct from regression_screenshot_url (the separate "still
  // broken" evidence image, never part of this list).
  images: z.array(feedbackImageSchema).default([]),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  // SPEC-feedback-return-path-2026-08-17 R4 (AC12): survives promotion --
  // present on the LINKED (triaged) detail read too. Optional, same reasoning
  // as the untriaged schema above.
  operator_question: z.string().nullable().optional(),
  operator_question_asked_at: z.string().nullable().optional(),
  reporter_answer: z.string().nullable().optional(),
  reporter_answered_at: z.string().nullable().optional(),
  // v0.17.16: the reporter said "still broken" after the newest ship move and
  // has not confirmed since (computed server-side from the exact stored time).
  still_broken_after_ship: z.boolean().optional(),
});
export type CoordinationLinkedFeedback = z.infer<typeof coordinationLinkedFeedbackSchema>;

export const coordinationWorkItemFeedbackResponseSchema = z.object({
  feedback: z.array(coordinationLinkedFeedbackSchema),
});
export type CoordinationWorkItemFeedbackResponse = z.infer<
  typeof coordinationWorkItemFeedbackResponseSchema
>;

export const coordinationPromoteResponseSchema = z.object({
  feedback_id: z.string(),
  work_item_id: z.string(),
  github_issue_number: z.number().nullable(),
  created: z.boolean(),
  // Non-null when the work item was created but the GitHub push was not. MUST be
  // declared here: fetchAPI's Zod parse strips undeclared keys silently, so
  // omitting it would leave the UI unable to see the failure it is meant to
  // report -- the field would arrive from the backend and vanish before render.
  github_error: z.string().nullable().optional(),
});
export type CoordinationPromoteResponse = z.infer<typeof coordinationPromoteResponseSchema>;

// CH-03 (v0.17 platform review): the existing backend repair-link endpoint
// (POST /api/coordination/work-items/{id}/repair-link,
// routers/coordination_view.py::repair_link_endpoint) had no frontend
// client or schema at all -- the operator toast pointed at a recovery path
// that did not exist in the UI. Mirrors RepairLinkResponse
// (schemas/stakeholder_portal_responses.py) field-for-field, same shape as
// coordinationPromoteResponseSchema above minus feedback_id.
export const coordinationRepairLinkResponseSchema = z.object({
  work_item_id: z.string(),
  github_issue_number: z.number().nullable(),
  created: z.boolean(),
  adopted: z.boolean(),
  already_bridged: z.boolean(),
  github_error: z.string().nullable().optional(),
});
export type CoordinationRepairLinkResponse = z.infer<typeof coordinationRepairLinkResponseSchema>;

export const coordinationPlanningResponseSchema = z.object({
  work_item: coordinationWorkItemRowSchema,
  mirrored: z.boolean(),
});
export type CoordinationPlanningResponse = z.infer<typeof coordinationPlanningResponseSchema>;

export const coordinationLinkResponseSchema = z.object({
  feedback_id: z.string(),
  linked_to: z.string(),
  should_close: z.boolean().nullable().optional(),
  github_synced: z.boolean().nullable().optional(),
  github_action: z.string().nullable().optional(),
  fanout_fired: z.boolean(),
});
export type CoordinationLinkResponse = z.infer<typeof coordinationLinkResponseSchema>;

// v0.17.15: POST /api/coordination/work-items/{id}/mark-shipped. Moves an
// item the bridge cannot see (unlinked merged PR, data repair) to "shipped,
// awaiting your confirmation". `evidence` echoes what went into the audit
// record (only the fields the operator gave are present).
export const coordinationShipEvidenceSchema = z.object({
  pr_number: z.number().optional(),
  commit_sha: z.string().optional(),
  release_tag: z.string().optional(),
  data_repair_note: z.string().optional(),
  pr_merge_check: z.string(),
  actor: z.string(),
  recorded_at: z.string(),
});
export const coordinationMarkShippedResponseSchema = z.object({
  work_item_id: z.string(),
  stage: z.string().nullable(),
  system_verification: z.string().nullable(),
  human_resolution: z.string().nullable(),
  claim_ladder: feedbackClaimLadderSchema,
  evidence: coordinationShipEvidenceSchema,
  reporters_notified: z.boolean(),
});
export type CoordinationMarkShippedResponse = z.infer<typeof coordinationMarkShippedResponseSchema>;

export const coordinationResolutionResponseSchema = z.object({
  work_item_id: z.string(),
  human_resolution: z.string().nullable(),
  resolution_note: z.string().nullable(),
});
export type CoordinationResolutionResponse = z.infer<typeof coordinationResolutionResponseSchema>;

// SPEC-feedback-return-path-2026-08-17 R4 (AC1/AC2): POST
// /api/coordination/feedback/{id}/question — ask a question on a report
// without promoting it or declaring an outcome. Works on an untriaged report.
export const coordinationAskQuestionResponseSchema = z.object({
  feedback_id: z.string(),
  operator_question: z.string(),
  operator_question_asked_at: z.string().nullable().optional(),
});
export type CoordinationAskQuestionResponse = z.infer<typeof coordinationAskQuestionResponseSchema>;

// v0.17.16: POST /api/coordination/feedback/{id}/dismiss takes an untriaged
// report off the board. No work item, no GitHub issue, no email.
export const coordinationDismissResponseSchema = z.object({
  feedback_id: z.string(),
  acceptance_state: z.string(),
  already_dismissed: z.boolean(),
});
export type CoordinationDismissResponse = z.infer<typeof coordinationDismissResponseSchema>;
